import type Phaser from 'phaser';
import { blankProfile, clearProfile, clearSaveSync, createSeed, FIRST_BATTLE_CARD_REWARD, grantStarterGaraimon, loadProfile, loadSaveSync, makeAIProfile, markSaveSynced, newGaraimon, rollCardReward, saveProfile, seededRandom, trainProfile, SIMULATION_VERSION, POLICY_VERSION } from './domain/game';
import { isStyleId, styleLabels } from './domain/styles';
import { FALLBACK_SPECIES_ID, continentNames, creaturesOn, getCreature, roster } from './domain/roster';
import type { AIProfile, ArenaFeatures, BattleEpisode, ContinentId, GaraimonInstance, PlayerProfile, RarityTier, StyleId } from './domain/types';
import { COLLECT_CONFIG, RARITY_LADDER, rarityRung } from './domain/collectConfig';
import { buildBoard, logicalHeightFor, rollBoardSeed, type BoardReserves, type CollectBoard } from './domain/collectBoard';
import { resultToRecipe, type CollectResult } from './domain/collectSim';
import { selectEncounter } from './domain/encounterSelection';
import { reportColourProfileProblems } from './domain/colourProfiles';
import { rollArena } from './domain/arena';
import { consumeGachaCard, finalizeBattle, isDeveloperAccount, readAccountSave, signInWithEmail, signOut, supabase, syncProfile, type AccountSave } from './platform/supabase';
import { profileHoldsProgress, resolveSignInSave } from './domain/signInSave';
import { DEV_FLAGS, hasDeveloperAccount, isDevMode, loadDevSettings, setDevFlag, setDeveloperAccount, type DevSettings } from './platform/devSettings';
import { startViewportDiagnostic, startViewportTracking } from './platform/viewport';
import { isMusicMuted, pickRandomBattleTrack, playBattleMusic, preloadBattleMusic, stopBattleMusic, toggleMusicMuted, unlockAudio, type BattleMusicTrack } from './platform/battleMusic';
import type { BattleScene } from './render/BattleScene';
import type { CollectProgress, CollectScene } from './render/CollectScene';
import { reelPortraitUrl, startPlaceholderPortraits } from './render/placeholderPortrait';
import { arenaCameraBounds, MAX_ZOOM, MIN_ZOOM } from './render/battleCamera';
import './style.css';

const root = document.querySelector<HTMLDivElement>('#app')!;
const desktop = matchMedia('(pointer:fine)').matches && innerWidth > 760 && !new URLSearchParams(location.search).has('preview');
let profile = loadProfile();

/**
 * Whether this visit is running against an account.
 *
 * Resolved once, before the first screen is drawn. Signing in returns through a
 * fresh page load, and signing out returns to the landing page, so this changes
 * within a page load only when the player signs out. The top bar reads it to
 * decide where the logo goes; GachID reads it to decide whether the
 * Account panel offers sign-in or sign-out.
 *
 * Sign-out is a confirmed product decision rather than an incidental behaviour:
 * it clears the device's local copy and returns the player to the landing page.
 * See `docs/decisions/DEC-006-sign-out.md`.
 */
let signedIn = false;

/**
 * The account whose save is waiting on the player to choose, or null.
 *
 * Set when both the device and the account hold progress and the device copy is not
 * known to be in the account. It is the parked half of "not now": the session stays
 * as it is, nothing is written to the account, and the player plays on their device
 * copy until they decide. The choice is then offered again on the next start, and
 * from GachID in the meantime, so deferring costs nothing but time.
 *
 * Nothing needs to be remembered for that to work: the state that produced the
 * question - a device copy not stamped into the account, and an account that holds
 * progress - is still true until the player chooses. See `src/domain/signInSave.ts`.
 */
let pendingAccountId: string | null = null;

/**
 * Pins the app shell to the region of the screen the browser is actually
 * showing, so the bottom menu is never left underneath the browser's own bar.
 * `dvh` alone cannot do this: it only accounts for chrome the browser chose to
 * shrink the layout for, and on some browsers the bar is simply painted over the
 * page. Started before the first screen is painted, so the shell is already the
 * right size rather than resizing once the first frame lands.
 *
 * `?devViewport=1` adds an on-screen readout of the raw numbers, for the case
 * where a browser reports its visible area in a way this does not anticipate.
 */
startViewportTracking();

/**
 * The board seed for the encounter in progress: what the player collects from.
 *
 * Held rather than stored on the profile, because it is only meaningful while an
 * encounter is open. Its recent history is on the profile so two encounters in a
 * row do not open on the same field.
 */
let boardSeed = createSeed();

/**
 * The arena seed for the encounter in progress, rolled independently of the
 * board. It is here, rather than read back off the mini-game, to keep the
 * decoupling visible: the arena never depends on what was collected.
 */
let arenaSeed = createSeed();

let collectGame: Phaser.Game | undefined;
/** The finished collection, kept so the encounter screen can show what was gathered. */
let activeBoardResult: CollectResult | undefined;
let activeArena: ArenaFeatures | undefined;
let battleGame: Phaser.Game | undefined;
let activeBattleId = '';
let activeBattlePartner: BattlePartner | undefined;
let candidateId = '';

/**
 * True while a battle is on screen and could still be retreated from.
 *
 * It is what turns any navigation into a guard: while it is set, every route out
 * of the battle screen asks before it abandons the fight, because leaving is a
 * retreat and grants no battle rewards.
 */
let battleInProgress = false;
/** The live battle scene, so leaving mid-battle retreats through the normal path. */
let activeBattleScene: BattleScene | undefined;
/** Where to continue after a confirmed retreat, set by the control the player used. */
let retreatDestination: (() => void) | undefined;

/**
 * The partner a battle will use.
 *
 * `instance` is the account's own Garaimon and is what gets trained afterwards.
 * It is absent when developer mode has fielded a species the account does not
 * own: that partner is temporary, so it is never written to the roster and never
 * trained. Modelling the temporary case explicitly, rather than by a separate
 * flag, means any caller that wants to save or train a partner can only do so
 * when the account actually owns it.
 */
interface BattlePartner {
  speciesId: string;
  profile: AIProfile;
  instance?: GaraimonInstance;
}
/** Guards against the battle result being handled twice. Reset for each battle. */
let battleResultHandled = false;

/**
 * Development-only URL overrides. They exist so a battle can be replayed
 * identically while one detail changes, which is how the style differences
 * required by `docs/MVP_EXECUTION_PLAN.md` acceptance criterion 3 are measured.
 * Documented in README.md:
 *
 *   `?devSeed=12345` fix the colour board seed and the arena seed
 *   `?devStyle=hunter` force the partner's style, whatever species it is
 *   `?devMusic=wild` force which battle track plays, so each can be heard
 *
 * They are held rather than read at module load because they are only allowed to
 * take effect for an account on the developer list, and that cannot be known until
 * the account has been resolved. `readDevOverrides` is the single place they are
 * parsed, and it is called only when the account is a developer. A release build
 * therefore ignores every one of them, whatever is in the address bar.
 */
const devParams = new URLSearchParams(location.search);
let devSeed: number | undefined;
let forcedStyle: StyleId | undefined;
let forcedMusicTrack: BattleMusicTrack | undefined;
/** Whether any override is active, so a finished battle knows to print its summary. */
let devOverridesActive = false;

/** Parses the developer-only URL overrides. Called once, and only for a developer account. */
function readDevOverrides(): void {
  const seedParam = devParams.get('devSeed');
  // Floored, because a seed is stored as a whole number and `?devSeed=1.5` is a value a
  // developer can plausibly type. Left fractional it would be carried into the stored
  // episode and the database would refuse the battle insert.
  devSeed = seedParam !== null && seedParam.trim() !== '' && Number.isFinite(Number(seedParam)) ? Math.floor(Number(seedParam)) : undefined;
  const styleParam = devParams.get('devStyle');
  forcedStyle = isStyleId(styleParam) ? styleParam : undefined;
  const musicParam = devParams.get('devMusic');
  forcedMusicTrack = musicParam === 'grassland' || musicParam === 'spark' || musicParam === 'wild' ? musicParam : undefined;
  devOverridesActive = devSeed !== undefined || forcedStyle !== undefined;
}

/**
 * Drops the parsed overrides. Called on sign-out, so they stop applying at the same
 * moment the developer account does rather than lasting until the next reload.
 */
function clearDevOverrides(): void {
  devSeed = undefined;
  forcedStyle = undefined;
  forcedMusicTrack = undefined;
  devOverridesActive = false;
}

/**
 * Prefix for the `data-summon` value of a developer-mode partner. A roster
 * button's value is normally the creature's own id, so the prefix is what
 * distinguishes "field this species for testing" from "field the Garaimon I
 * actually own".
 */
const DEV_SUMMON_PREFIX = 'dev:';

/** Every ability in the game that raises a guard, so a battle log can be read for them. */
const guardAbilityIds = new Set(roster.flatMap((creature) => creature.abilities).filter((ability) => ability.effect === 'guard').map((ability) => ability.id));

/**
 * Picks the two seeds for the next encounter: the board the player collects from
 * and the arena the battle happens in.
 *
 * They are rolled together but used independently, which is the whole point of
 * the change: the board decides *which* Garaimon appears and nothing about the
 * ground. Keeping them separate also means either can be reported and replayed
 * on its own.
 *
 * Without a `?devSeed` override the board seed is re-rolled until it is not one
 * the player has just seen, so consecutive encounters do not open on the same
 * field. The recent list holds seeds rather than terrain patterns, because the
 * board is described by its seed now and not by terrain at all.
 */
function rollEncounterSeeds(): { board: number; arena: number } {
  if (devSeed !== undefined) return { board: devSeed, arena: devSeed };
  return { board: rollBoardSeed(profile.recentBoardSeeds, createSeed), arena: createSeed() };
}

/**
 * The AI profile to give the partner, or `undefined` to let the battle use its
 * own default. Applies the development style override so one species can be
 * replayed under different styles.
 */
function partnerProfile(instance: GaraimonInstance | undefined): AIProfile | undefined {
  if (!forcedStyle) return instance?.aiProfile;
  return makeAIProfile(forcedStyle, (activeArena?.seed ?? 0) ^ 35);
}

/**
 * Reads an episode's recorded events into the numbers the style comparison
 * needs. Damage values are already percentages of maximum HP, so the damage a
 * fighter had taken when it first chose to guard reveals which self-preservation
 * threshold its style uses.
 */
function summariseEpisode(episode: BattleEpisode) {
  let damageTakenByPlayer = 0;
  let hpLostBeforeFirstGuard: number | null = null;
  let guardUses = 0;
  let dodges = 0;
  let basicStrikes = 0;
  const abilityUses: Record<string, number> = {};
  for (const event of episode.events) {
    if (event.type === 'hit' && event.targetId === 'player' && event.actorId !== 'player' && typeof event.value === 'number' && event.value > 0) damageTakenByPlayer += event.value;
    if (event.type === 'dodge' && event.actorId === 'player') dodges += 1;
    if (event.type !== 'ability-start' || event.actorId !== 'player') continue;
    const id = event.abilityId ?? 'unknown';
    abilityUses[id] = (abilityUses[id] ?? 0) + 1;
    if (id.endsWith('-basic')) basicStrikes += 1;
    if (guardAbilityIds.has(id)) {
      guardUses += 1;
      if (hpLostBeforeFirstGuard === null) hpLostBeforeFirstGuard = Math.round(damageTakenByPlayer);
    }
  }
  return {
    style: episode.participants[1]?.aiProfile.style,
    outcome: episode.outcome,
    seconds: Math.round(episode.durationMs / 1000),
    guardUses,
    hpBeforeFirstGuard: hpLostBeforeFirstGuard === null ? null : 100 - hpLostBeforeFirstGuard,
    dodges,
    basicStrikes,
    abilityUses,
  };
}

/**
 * Tears the Phaser game down. This is called from inside Phaser's own update
 * loop, so the destroy is deferred to the next frame.
 */
function teardownBattleGame() {
  const game = battleGame;
  battleGame = undefined;
  if (!game) return;
  requestAnimationFrame(() => game.destroy(true));
}

/**
 * Tears the mini-game's Phaser game down.
 *
 * Same deferral as the battle: the attempt finishes inside the scene's own
 * update, and destroying a Phaser game from within its loop is not safe.
 */
function teardownCollectGame() {
  const game = collectGame;
  collectGame = undefined;
  if (!game) return;
  requestAnimationFrame(() => game.destroy(true));
}

/**
 * How the account save is going, as the player is told it.
 *
 * `idle` is the normal state and shows nothing at all: a notice that is always
 * there is furniture, not information. `unsaved` means progress is on this device
 * but has not reached the account. `recovered` is the moment the account catches
 * up, and it clears itself.
 */
type SaveStatus = 'idle' | 'unsaved' | 'recovered';
let saveStatus: SaveStatus = 'idle';
let saveStatusTimer: number | undefined;

/** How long "Data saved." stays up. Long enough to read, short enough not to nag. */
const SAVE_NOTICE_MS = 3000;

/**
 * Shows, changes or clears the save notice.
 *
 * The notice is written into a slot that `shell` puts in every screen, so it
 * survives navigation - a warning built by one screen would vanish the moment the
 * player changed screen, which is worse than not showing it at all.
 */
function setSaveStatus(status: SaveStatus) {
  if (saveStatusTimer !== undefined) { clearTimeout(saveStatusTimer); saveStatusTimer = undefined; }
  saveStatus = status;
  renderSaveNotice();
}

function renderSaveNotice() {
  const slot = root.querySelector<HTMLElement>('#save-notice');
  if (slot) slot.innerHTML = saveNoticeMarkup();
}

function saveNoticeMarkup(): string {
  if (saveStatus === 'idle') return '';
  if (saveStatus === 'recovered') {
    return '<div class="save-notice save-notice-ok"><img class="save-notice-icon" src="/branding/status-saved.png" alt="" /><span>Data saved.</span></div>';
  }
  return '<div class="save-notice save-notice-warn"><img class="save-notice-icon" src="/branding/status-offline.png" alt="" /><span>Server lost. Unable to save progress.</span></div>';
}

/**
 * A save reached the account.
 *
 * "Data saved." is only announced when there was something to recover from, so an
 * ordinary save is silent. On recovery the notice clears itself after
 * `SAVE_NOTICE_MS`; while unsaved it stays until the account catches up.
 */
function noteSaveSucceeded() {
  const wasUnsaved = saveStatus === 'unsaved';
  if (saveStatusTimer !== undefined) { clearTimeout(saveStatusTimer); saveStatusTimer = undefined; }
  if (!wasUnsaved) { saveStatus = 'idle'; renderSaveNotice(); return; }
  saveStatus = 'recovered';
  renderSaveNotice();
  saveStatusTimer = window.setTimeout(() => { saveStatus = 'idle'; saveStatusTimer = undefined; renderSaveNotice(); }, SAVE_NOTICE_MS);
}

/** One attempt at the account write. The error is returned rather than thrown. */
async function attemptProfileSync(): Promise<{ accountId: string | null; error: unknown | null }> {
  try {
    return { accountId: await syncProfile(profile), error: null };
  } catch (error) {
    return { accountId: null, error };
  }
}

/**
 * Records that the device copy and the account now hold the same progress.
 *
 * Called whenever that becomes true - a save that landed, or a device copy replaced
 * by the account's - so that the next start knows there is nothing to choose between.
 * A save that fails removes the stamp instead, which is what brings the question back
 * when the account turns out to be missing work. See `loadSaveSync` in
 * `src/domain/game.ts`.
 */
function engageAccount(accountId: string) {
  signedIn = true;
  markSaveSynced(accountId);
}

/**
 * Pushes progress to the account and records whether it arrived.
 *
 * Returns whether the account now holds this progress. A failure is recorded rather
 * than thrown, because the caller has already written the local copy and no work is
 * lost at this point; what matters is that the failure becomes visible and that
 * sign-out can refuse to delete a copy the account does not have.
 *
 * The warning waits for two failures in a row, not one. A single failed write is
 * usually a blip, and a marker that appears on every blip is one players learn to
 * ignore - which is the opposite of what it is for. The retry is immediate rather
 * than a timer, because the player is already waiting on this save: making them act
 * once more to be told about the first failure would hide it until it was too late.
 *
 * `quiet` suppresses the notice and only logs. It is for the attempts the player did
 * not ask for - the transfer when signing in for the first time, and the last save
 * before sign-out - because a notice raised by simply opening the app is noise, and
 * neither of those moments is one the player can act on. A failure still drops the sync
 * stamp either way, so the next start asks rather than assumes.
 */
async function pushProfileToAccount({ quiet = false }: { quiet?: boolean } = {}): Promise<boolean> {
  // A player without an account has no account to write to. Gating on that, rather
  // than on the cloud merely being configured, is what stops "unable to save" being
  // raised against a player who never asked for a save.
  if (!supabase || !signedIn) { setSaveStatus('idle'); return true; }
  const first = await attemptProfileSync();
  if (!first.error && first.accountId) { engageAccount(first.accountId); noteSaveSucceeded(); return true; }
  const second = await attemptProfileSync();
  if (!second.error && second.accountId) { engageAccount(second.accountId); noteSaveSucceeded(); return true; }
  console.warn('Progress is saved on this device but has not reached the account.', first.error ?? 'the session had ended', second.error ?? 'the session had ended');
  // The account may now be missing this copy, so the device copy is no longer known to
  // be in it. Dropping the stamp is what makes the next start ask the player rather
  // than assume the two agree. Only ever reached while the account is engaged, where the
  // stamp is this account's or absent - `markSaveSynced` writes the engaged account and
  // nothing else - so this can never discard a record of a different account's copy.
  clearSaveSync();
  if (!quiet) setSaveStatus('unsaved');
  renderHud();
  return false;
}

const save = async () => { saveProfile(profile); await pushProfileToAccount(); renderHud(); };

/**
 * The start-up work that runs when an account is the copy in use.
 *
 * Kept separate from the decision above it, because reaching the map is the same
 * whichever way the choice went.
 */
async function finishAccountStart(): Promise<void> {
  // The testing helpers are for an account on the developer list only. A check
  // that fails leaves them off and says so, rather than leaving them on for
  // everyone or removing them without explanation.
  try {
    setDeveloperAccount(await isDeveloperAccount());
  } catch (error) {
    console.error('Garaimon: the developer list could not be read, so developer mode stays off.', error);
    setDeveloperAccount(false);
  }
  if (hasDeveloperAccount()) {
    readDevOverrides();
    if (devParams.has('devViewport')) startViewportDiagnostic();
  }
  // Straight to the map. There is no first-run step to resume: a signed-in
  // player is already under way, and a new account starts on Explore too.
  renderHome();
}

/** Makes the account's copy the one in use, and records that the two now agree. */
function takeAccountCopy(copy: PlayerProfile, accountId: string): void {
  profile = copy;
  saveProfile(copy);
  engageAccount(accountId);
}

/**
 * Reconciles the device's progress with the account's when someone signs in.
 *
 * The device keeps one save slot while the account keeps its own, so when both hold
 * progress, using one discards the other. Which of those cases this is - and whether
 * the player has to be asked - is decided in `src/domain/signInSave.ts`, where the
 * rule is written down and tested on its own; this function only carries it out.
 *
 * The account is always read, even when the answer looks predictable. Reading is what
 * brings the copy a player changed on another device, and a start-up that skipped it
 * would keep a stale device copy and then write that over the newer one.
 */
async function startWithAccount(accountId: string): Promise<void> {
  // Held off for the whole comparison, not just until one of the branches below decides.
  // While this is reading the account, a save arriving from the game must not be able to
  // write the device copy up: that is the overwrite this function exists to prevent, and
  // the branch that follows may be about to decide the account's copy is the one to keep.
  signedIn = false;
  const device = loadProfile();
  let accountSave: AccountSave | null = null;
  let accountUnreadable = false;
  try {
    accountSave = await readAccountSave(device);
  } catch (error) {
    accountUnreadable = true;
    console.warn('Garaimon: the account could not be read, so nothing will be written to it yet.', error);
  }
  const resolution = resolveSignInSave({
    deviceHoldsProgress: profileHoldsProgress(device),
    accountUnreadable,
    accountHoldsProgress: accountSave?.profile ? profileHoldsProgress(accountSave.profile) : false,
    syncedAccountId: loadSaveSync(),
    accountId,
  });
  switch (resolution) {
    case 'load-account':
      // The account is the copy being kept. Where the read succeeded that is the
      // account's own copy; where it did not, the device copy is the account's copy by
      // the stamp's own account - which is what makes starting offline safe.
      takeAccountCopy(accountSave?.profile ?? device, accountId);
      await finishAccountStart();
      return;
    case 'keep-device':
      // A new account, so there is no second copy to weigh and nothing to ask. The
      // progress already on the device is kept and written up, which is what a player
      // who has been playing without an account expects to happen.
      profile = device;
      saveProfile(device);
      engageAccount(accountId);
      // Quiet, because this is an attempt the player did not ask for - the same kind of
      // start-up connection DEC-010 keeps silent. If the account is really unreachable,
      // the stamp is dropped here anyway and the player's next own change raises the
      // notice, which is where DEC-010 puts it.
      void pushProfileToAccount({ quiet: true });
      await finishAccountStart();
      return;
    case 'discard-device':
      console.warn('Garaimon: the progress on this device belongs to a different account, so it has been removed rather than offered to this one.');
      clearProfile();
      takeAccountCopy(accountSave?.profile ?? blankProfile(), accountId);
      await finishAccountStart();
      return;
    case 'hold':
      // Nothing is decided and nothing is written. The device copy is played, the
      // account is left exactly as it is, and the comparison is tried again later.
      pendingAccountId = accountId;
      signedIn = false;
      renderSaveChoice({ kind: 'hold' });
      return;
    case 'unreachable':
      pendingAccountId = accountId;
      signedIn = false;
      renderSaveChoice({ kind: 'unreachable' });
      return;
    case 'ask':
      signedIn = false;
      renderSaveChoice({ kind: 'ask', device, account: accountSave!.profile!, accountId });
      return;
  }
}

/**
 * Tries the account again once the browser reports the connection is back.
 *
 * This is what makes "not now" a postponement rather than a refusal. It is skipped
 * during a battle, where a screen arriving over the arena would be worse than waiting
 * for the next start, and it does nothing at all unless a choice is still owed.
 */
async function retryAccountDecision(): Promise<void> {
  const accountId = pendingAccountId;
  if (!accountId || battleInProgress) return;
  pendingAccountId = null;
  await startWithAccount(accountId);
}

window.addEventListener('online', () => void retryAccountDecision());

const showDesktopGate = () => { root.innerHTML = '<main class="gate"><img class="brand-logo gate-logo" src="/branding/garaimon-logo-full.png" alt="Garaimon" /><h1>Your adventure<br>is on mobile.</h1><p>Open Garaimon on your phone to explore and play.</p><span class="gate-url">garaimon.com</span></main>'; };
/**
 * Paints the app chrome around one screen.
 *
 * The wordmark is centred on the bar itself, not by the flex layout. Left to the
 * layout it slides sideways whenever a neighbour changes width, and two screens
 * change it: the map hides the back button, and the landing screen hides the card
 * pill. Pinning the logo to the bar's centre keeps it in one place everywhere.
 *
 * The logo is also a button: it returns a signed-in player to the map, and
 * everyone else to the page that explains the game.
 *
 * `landing` is the one screen that is a single full-bleed picture. It keeps the
 * bar so the logo stays in one place, but drops the bottom menu, which would
 * otherwise cover the bottom of the picture, and hides the back button, which has
 * nowhere useful to go from the screen the site opens on.
 */
/**
 * The top bar's right-hand side. Shared by `shell` and `renderHud`, so the "not saved"
 * marker survives navigation. Every screen is painted by `shell`, so a warning built
 * only by `renderHud` would vanish the moment the player changed screen - which is
 * worse than not showing it at all.
 */
function topMetaMarkup(): string {
  return `<span class="pill" aria-label="${profile.gachaCards} GachaCards">✦ ${profile.gachaCards}<small> CARDS</small></span>`;
}

function shell(content: string, title = 'Garaimon', variant?: 'landing') {
  const landing = variant === 'landing';
  const eyebrow = landing ? '' : `<p class="eyebrow">${title}</p>`;
  const nav = landing ? '' : '<nav class="bottom-nav"><button data-screen="home"><img class="nav-icon" src="/branding/tab-explore.png" alt="" /><small>Explore</small></button><button data-screen="collection"><img class="nav-icon" src="/branding/tab-garaimon.png" alt="" /><small>Garaimon</small></button><button data-screen="profile"><img class="nav-icon" src="/branding/tab-gachid.png" alt="" /><small>GachID</small></button></nav>';
  root.innerHTML = `<div class="app-shell${landing ? ' landing-shell' : ''}"><header class="topbar"><button class="icon-btn" id="back" aria-label="Back">←</button><button class="wordmark" id="brand-home" type="button" aria-label="Garaimon home"><img class="brand-logo topbar-logo" src="/branding/garaimon-logo-full.png" alt="Garaimon" /></button><div class="top-meta">${topMetaMarkup()}</div><div class="save-notice-slot" id="save-notice" role="status" aria-live="polite">${saveNoticeMarkup()}</div></header><main class="screen">${eyebrow}${content}</main>${nav}</div>`;
  // Every route out of a screen goes through here, so a battle in progress can ask
  // before it is abandoned instead of being left running behind another screen.
  const navigate = (destination: () => void) => { if (battleInProgress) confirmRetreat(destination); else destination(); };
  root.querySelector('#back')?.addEventListener('click', () => navigate(renderHome));
  root.querySelector('#brand-home')?.addEventListener('click', () => navigate(signedIn ? renderHome : renderLanding));
  root.querySelectorAll<HTMLButtonElement>('[data-screen]').forEach((button) => button.addEventListener('click', () => { const target = button.dataset.screen; navigate(() => target === 'home' ? renderHome() : target === 'collection' ? renderCollection() : renderProfile()); }));
}
function renderHud() {
  const el = root.querySelector('.top-meta');
  if (el) el.innerHTML = topMetaMarkup();
}

/**
 * The reason a sign-in link did not work, read from the address the browser
 * arrived on, or `undefined` for an ordinary visit.
 *
 * This is read from the URL rather than from the auth client on purpose. On a
 * failed link the client reports the reason only from its own initialisation,
 * which the constructor discards, so `getSession()` afterwards returns no error at
 * all and asking it would leave the failure invisible. Supabase writes the reason
 * into the URL fragment (`#error=access_denied&error_code=otp_expired&...`); the
 * query string is read as well, because a callback can be configured to use it.
 *
 * `message` is shown to the player and is therefore one fixed sentence per case.
 * The raw reason is returned separately and only ever logged: it is untrusted text
 * from the address bar, so it must never be drawn.
 */
function authLinkFailure(): { message: string; detail: string } | undefined {
  const query = new URLSearchParams(location.search);
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
  const read = (key: string) => fragment.get(key) ?? query.get(key);
  const code = read('error_code') ?? read('error');
  const description = read('error_description');
  if (!code && !description) return undefined;
  return {
    message: code === 'otp_expired'
      ? 'That sign-in link has expired or was already used. Enter your email to get a new one.'
      : 'Sign-in did not complete. Enter your email to try again.',
    detail: `error=${code ?? '(none)'} description=${description ?? '(none)'}`,
  };
}

/**
 * Strips the sign-in failure out of the address bar, so a reload shows the
 * ordinary landing page instead of reporting the same spent link again. Only the
 * failure keys are removed; anything else in the address, such as `?preview`, is
 * left exactly as it was.
 */
function clearAuthLinkParams(): void {
  const query = new URLSearchParams(location.search);
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
  for (const key of ['error', 'error_code', 'error_description', 'error_description_original']) {
    query.delete(key);
    fragment.delete(key);
  }
  const search = query.toString();
  const hash = fragment.toString();
  history.replaceState(null, '', `${location.pathname}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`);
}

/**
 * A sentence shown on the sign-in card.
 *
 * Only failures reach this region. It once carried statements of fact as well - a link
 * that had been sent, and a sign-out whose save had failed - but a statement of fact
 * does not need a box drawn around it: the send button's own label reports a link that
 * went out, and the sign-out case is covered by the choice the next sign-in asks for
 * (see `docs/decisions/DEC-011`). What is left is what a player has to act on, so it is
 * always drawn as a failure and always announced as one.
 */
type LandingNotice = { message: string };

/** The countdown holding the send button after a refusal. Module scope so a rebuild can stop it. */
let landingCooldown: number | undefined;

/**
 * Turns a failed send into something a player can act on.
 *
 * Supabase's own wording is written for a developer - "429: email rate limit exceeded"
 * - so it is never shown. The client exposes the HTTP status and a short error code,
 * which is what these cases are told apart by; anything unrecognised keeps a plain
 * sentence rather than the raw text.
 *
 * The refused-send sentence deliberately names no particular limit, because the one
 * error code covers two rules that need different waits: the whole project's hourly
 * allowance, and the shorter interval allowed between messages to one address. The
 * hourly allowance is what this project hits today - it is set to 2 messages an hour
 * while Supabase's own mail service is in use, and it cannot be lifted without custom
 * SMTP. The interval rule is what will remain once custom SMTP is configured, and it
 * wants a wait of about a minute. Wording that named the hourly allowance would
 * therefore be wrong for the only refusal most players will ever see.
 *
 * The countdown is likewise about a minute rather than the true hourly window: that
 * window cannot be read from here, so the timer exists to stop the button inviting a
 * tap that cannot work, not to promise an exact reset.
 */
function describeSendFailure(error: unknown): { notice: LandingNotice; waitSeconds?: number } {
  const status = (error as { status?: number } | null)?.status;
  const code = (error as { code?: string } | null)?.code ?? '';
  const message = error instanceof Error ? error.message : '';
  if (status === 429 || code.includes('rate_limit') || message.includes('rate limit')) {
    return {
      notice: { message: 'Too many sign-in emails have been requested just now. Wait about a minute, then try again.' },
      waitSeconds: 60,
    };
  }
  if (code === 'email_address_not_authorized' || message.includes('not authorized')) {
    return { notice: { message: 'A sign-in email cannot be sent to that address at the moment. You can still play without an account.' } };
  }
  return { notice: { message: 'The sign-in link could not be sent. Try again in a moment.' } };
}

/**
 * The landing screen: one full-bleed pixel picture with the sign-in card floating
 * near its top, where the sky is emptiest and nothing the player needs is hidden.
 *
 * The card is kept to the smallest thing that still does its job: one sentence,
 * the email field, and the two ways on. There is deliberately no heading and no
 * visible field label - the logo above names the screen, and the sentence already
 * says what the field is for - so the picture stays the thing the player looks at.
 * The field still carries an `aria-label`, so it is named for a screen reader even
 * though the name is not drawn.
 *
 * Both routes are always offered, whether or not cloud accounts are configured.
 * The email link is the way to keep progress against an account; the quiet button
 * underneath is the way past this screen for a player who does not want one, so
 * an account is never the price of reaching the map. Hiding it when accounts are
 * available would make sign-in mandatory by accident rather than by decision.
 *
 * The button is a control, not a message. Its label only ever describes what the
 * button is doing - "Send sign-in link", "Sending…", "Link sent", or a countdown
 * after a refusal - and every sentence a player has to read appears in one message
 * region at the top of the card. That is the pattern the GOV.UK Design System uses,
 * and it stops a failure being mistaken for the button's own wording.
 *
 * A failed send is described in that region rather than only in the console, which is
 * how it was before: the button read "Not sent · retry", which a player could see but
 * which explained nothing, and because the retry failed again in the same instant the
 * button looked like it did nothing at all.
 *
 * Nothing here is meant to scroll. The card is the only content, so there is no
 * second screenful to reach. Reached on a first visit, on every later signed-out
 * visit including one where a local save already exists, and from a failed
 * sign-in callback. That entry rule is a confirmed decision, not an inherited
 * default: see `docs/decisions/DEC-005-returning-local-player.md`.
 *
 * A signed-in player reaches this screen only by signing out, which returns them
 * here (see `docs/decisions/DEC-006-sign-out.md`). The screen offers no back
 * button: the site opens here, and there is nowhere behind it to go.
 *
 * `notice` is the message to show on arrival: a sign-in link that did not work, or a
 * sign-out that could not save first. Those two arrive with the page, because their
 * cause is in the address bar or in the session that has just ended.
 */
/**
 * The card shown when the device's progress and the account's cannot both be kept.
 *
 * It is deliberately in the app's own hand rather than a browser `confirm`: it has to
 * say what each copy holds, and a native dialog cannot. It is also the only safeguard
 * there is - whichever copy loses is gone - which is why nothing is written until a
 * button is pressed, why no button is primary over the other, and why the way out
 * explains what leaving does rather than only closing.
 */
function saveSummary(copy: PlayerProfile): string {
  // Named `copy` rather than `profile`, which is the module's own live profile: this
  // reads one of the two saves on the card, and shadowing that name would invite a
  // future edit to read the wrong one.
  const region = continentNames[copy.continent] ?? copy.continent;
  // Counted rather than fixed: "1 battles" and "1 GachaCards" read as a fault, and a
  // player comparing two saves is reading these numbers closely.
  const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  return [plural(copy.episodes.length, 'battle', 'battles'), plural(copy.garaimon.length, 'Garaimon', 'Garaimon'), plural(copy.gachaCards, 'GachaCard', 'GachaCards'), region].join(' · ');
}

type SaveChoice =
  | { kind: 'ask'; device: PlayerProfile; account: PlayerProfile; accountId: string }
  | { kind: 'hold' }
  | { kind: 'unreachable' };

function renderSaveChoice(choice: SaveChoice) {
  // The close control. Its label says what closing does, because an ✕ on its own could
  // be read as "cancel the sign-in" when it actually means "keep playing on this device
  // and decide later".
  const close = '<button class="save-choice-close" id="save-choice-close" type="button" aria-label="Not now — keep playing on this device">✕</button>';
  let body: string;
  if (choice.kind === 'ask') {
    body = `${close}<p class="step">SIGNED IN</p><h1>Which save<br>do you want?</h1><p class="save-choice-lead">This device and your account both hold progress. The one you do not pick is replaced.</p><div class="save-choice-side"><b>This device</b><span>${saveSummary(choice.device)}</span></div><div class="save-choice-side"><b>Your account</b><span>${saveSummary(choice.account)}</span></div><button class="primary" id="save-choice-account" type="button">Load account save <span>→</span></button><button class="primary save-choice-overwrite" id="save-choice-device" type="button">Overwrite with current progress <span>→</span></button><p class="save-choice-note">Closing this keeps this device's progress and leaves your account untouched. You will be asked again next time you open Garaimon.</p>`;
  } else if (choice.kind === 'hold') {
    body = `${close}<p class="step">SIGNED IN</p><h1>Your account<br>could not be reached</h1><p class="save-choice-lead">You are playing the progress saved on this device. Nothing is being sent to your account until you choose what to keep, and nothing is lost while you wait.</p><button class="primary" id="save-choice-retry" type="button">Try again <span>→</span></button><button class="primary" id="save-choice-continue" type="button">Continue on this device <span>→</span></button>`;
  } else {
    body = `${close}<p class="step">SIGNED IN</p><h1>Your account<br>could not be reached</h1><p class="save-choice-lead">There is no progress saved on this device, so there is nothing to open yet. Try again once you have a connection.</p><button class="primary" id="save-choice-retry" type="button">Try again <span>→</span></button>`;
  }
  shell(`<section class="save-choice" aria-label="Choose which save to keep">${body}</section>`, 'WELCOME', 'landing');

  root.querySelector('#save-choice-close')?.addEventListener('click', () => {
    // Closing is the deferral: the choice is still owed, so it is offered again on the
    // next start and from GachID in the meantime.
    if (choice.kind === 'ask') pendingAccountId = choice.accountId;
    renderHome();
  });
  root.querySelector('#save-choice-retry')?.addEventListener('click', () => {
    const accountId = pendingAccountId;
    pendingAccountId = null;
    if (accountId) void startWithAccount(accountId);
  });
  root.querySelector('#save-choice-continue')?.addEventListener('click', () => renderHome());
  if (choice.kind === 'ask') {
    root.querySelector('#save-choice-account')?.addEventListener('click', () => {
      takeAccountCopy(choice.account, choice.accountId);
      void finishAccountStart();
    });
    root.querySelector('#save-choice-device')?.addEventListener('click', () => {
      // The device copy is already the one in use, so it is left exactly as it is; the
      // account is the copy that gets replaced.
      profile = choice.device;
      saveProfile(choice.device);
      engageAccount(choice.accountId);
      void pushProfileToAccount();
      void finishAccountStart();
    });
  }
}

function renderLanding(notice?: LandingNotice) {
  // A countdown belongs to the button it was started on, so one left over from a
  // previous landing screen is stopped before a new card is drawn.
  if (landingCooldown !== undefined) window.clearInterval(landingCooldown);
  landingCooldown = undefined;
  shell(`<section class="login-card" aria-label="Sign in"><p class="login-notice" id="login-notice" role="alert" hidden></p><p>Enter email to create account/sign-in in order to save game data.</p><form id="login-form"><input id="email" type="email" aria-label="Email address" placeholder="you@example.com" required autocomplete="email"><button class="primary" id="login-submit" type="submit"><span class="login-submit-label">Send sign-in link</span><span class="login-submit-arrow">→</span></button></form><button class="text-button" id="demo">Or play without saved data</button></section>`,'WELCOME','landing');
  const submit = root.querySelector<HTMLButtonElement>('#login-submit');
  const setLabel = (text: string) => { const label = submit?.querySelector('.login-submit-label'); if (label) label.textContent = text; };
  const noticeEl = root.querySelector<HTMLElement>('#login-notice');
  // Written as text rather than markup: a sentence is the only thing that ever reaches
  // this region, and building it from text nodes keeps anything a player typed into
  // the field from being parsed as HTML.
  const showNotice = (message: string) => {
    if (!noticeEl) return;
    noticeEl.textContent = '';
    // Spoken rather than drawn: a sighted player reads the failure from the red box.
    const prefix = document.createElement('span');
    prefix.className = 'visually-hidden';
    prefix.textContent = 'Error: ';
    noticeEl.append(prefix);
    noticeEl.append(message);
    noticeEl.className = 'login-notice';
    noticeEl.hidden = false;
  };
  const hideNotice = () => { if (noticeEl) { noticeEl.textContent = ''; noticeEl.hidden = true; } };
  if (notice) showNotice(notice.message);
  root.querySelector<HTMLFormElement>('#login-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    // A second tap while the first is still in flight would send a second link.
    if (!submit || submit.disabled) return;
    const email = root.querySelector<HTMLInputElement>('#email')!.value;
    hideNotice();
    setLabel('Sending…');
    submit.disabled = true;
    try {
      await signInWithEmail(email);
      // Left disabled on purpose. The mail service allows only a couple of messages an
      // hour for the whole project, so a second send would spend another of them and
      // arrive as a second link that cannot be used.
      // No message. The button's own label saying "Link sent" is the whole of what the
      // player needs, and a paragraph in a yellow box explaining where to look for an
      // email was more noise than the moment deserved.
      setLabel('Link sent');
    } catch (error) {
      console.error('Garaimon: the sign-in link could not be sent.', error);
      const failure = describeSendFailure(error);
      showNotice(failure.notice.message);
      if (failure.waitSeconds === undefined) {
        setLabel('Send sign-in link');
        submit.disabled = false;
        return;
      }
      // Count down the wait so the button cannot invite a tap that cannot succeed.
      let remaining = failure.waitSeconds;
      setLabel(`Try again in ${remaining}s`);
      landingCooldown = window.setInterval(() => {
        // The card can be left mid-countdown, which detaches the button. The timer
        // stops itself rather than ticking on against a node nobody can see.
        if (!submit.isConnected) { window.clearInterval(landingCooldown); landingCooldown = undefined; return; }
        remaining -= 1;
        if (remaining > 0) { setLabel(`Try again in ${remaining}s`); return; }
        window.clearInterval(landingCooldown);
        landingCooldown = undefined;
        submit.disabled = false;
        setLabel('Send sign-in link');
      }, 1000);
    }
  });
  // Carries on with whatever is saved on this device, straight to the map. This
  // is the same continuation a signed-in player gets, so the local journey is
  // identical once it is under way; the only difference is that it starts here
  // rather than after a sign-in.
  root.querySelector('#demo')?.addEventListener('click', renderHome);
}

function renderHome() {
  // Whichever screen was open, its game goes with it. The back button replaces
  // the page under a running Phaser game, which would otherwise keep animating
  // into a container that is no longer on screen.
  teardownCollectGame();
  teardownBattleGame();
  shell(`<div class="world-map"><svg viewBox="0 0 360 170" role="group" aria-label="Choose a continent on the pixel map"><g class="map-hitareas" aria-hidden="true"><path data-continent="north-america" d="M28 28h23v-8h33v7h28v9h15v16h-9v12h-13v12H92v10H76V74H65V61H49V48H35z"/><path data-continent="south-america" d="M106 88h19v9h13v15h-7v14h-8v18h-10v-16h-6v-18h-8V98h7z"/><path data-continent="europe" d="M166 44h14v-7h20v6h9v10h-8v9h-12v-5h-14v-7h-9z"/><path data-continent="africa" d="M174 66h21v5h15v15h-8v16h-8v17h-10v-11h-8v-15h-8V78h6z"/><path data-continent="asia" d="M214 32h24v-8h31v6h23v7h28v9h17v14h-15v9h-19v7h-19V68h-17V61h-17v9h-17V58h-18z"/><path data-continent="oceania" d="M291 111h15v-6h19v7h12v10h-9v7h-15v-5h-13v-6h-9z"/></g><path class="map-land north" data-continent="north-america" role="button" tabindex="0" aria-label="Explore North America" d="M28 28h23v-8h33v7h28v9h15v16h-9v12h-13v12H92v10H76V74H65V61H49V48H35z"/><path class="map-land south" data-continent="south-america" role="button" tabindex="0" aria-label="Explore South America" d="M106 88h19v9h13v15h-7v14h-8v18h-10v-16h-6v-18h-8V98h7z"/><path class="map-land europe" data-continent="europe" role="button" tabindex="0" aria-label="Explore Europe" d="M166 44h14v-7h20v6h9v10h-8v9h-12v-5h-14v-7h-9z"/><path class="map-land africa" data-continent="africa" role="button" tabindex="0" aria-label="Explore Africa" d="M174 66h21v5h15v15h-8v16h-8v17h-10v-11h-8v-15h-8V78h6z"/><path class="map-land asia" data-continent="asia" role="button" tabindex="0" aria-label="Explore Asia" d="M214 32h24v-8h31v6h23v7h28v9h17v14h-15v9h-19v7h-19V68h-17V61h-17v9h-17V58h-18z"/><path class="map-land oceania" data-continent="oceania" role="button" tabindex="0" aria-label="Explore Oceania" d="M291 111h15v-6h19v7h12v10h-9v7h-15v-5h-13v-6h-9z"/><path class="map-island" d="M158 70h4v5h-4zM146 78h3v3h-3zM337 94h4v4h-4zM68 99h3v4h-3z"/><g class="map-labels" aria-hidden="true"><text x="47" y="53">NORTH AMERICA</text><text x="90" y="152">SOUTH AMERICA</text><text x="154" y="36">EUROPE</text><text x="169" y="92">AFRICA</text><text x="251" y="57">ASIA</text><text x="281" y="144">OCEANIA</text></g></svg><span class="map-note">PICK A REGION TO EXPLORE</span></div>`, 'EXPLORE');
  root.querySelectorAll<HTMLButtonElement>('[data-continent]').forEach((button) => button.addEventListener('click', () => { profile.continent = button.dataset.continent as ContinentId; void save(); void beginCollect().catch(reportCollectFailure); }));
  root.querySelectorAll<SVGPathElement>('.map-land').forEach((region) => region.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); region.dispatchEvent(new Event('click', { bubbles: true })); } }));
}

/**
 * Opens the pre-battle mini-game for the current continent.
 *
 * The screen is painted before the board is built, because the board's height
 * comes from the space the canvas actually gets and that cannot be measured
 * until the element exists. The scene is then handed a finished board and never
 * rolls one of its own: one seed, one field, reproducible from the seed alone.
 */
async function beginCollect() {
  // The profiles are compiled-in data, so a mismatch would be a build problem
  // rather than a runtime one. Checked here, before the screen is shown, so it is
  // reported once and in plain terms instead of surfacing as odd targeting.
  reportColourProfileProblems();
  const seeds = rollEncounterSeeds();
  boardSeed = seeds.board;
  arenaSeed = seeds.arena;
  activeBoardResult = undefined;
  renderCollectScreen();
  const mount = root.querySelector<HTMLElement>('#collect-canvas');
  if (!mount) throw new Error('Garaimon: the collection screen was painted without a canvas to mount the game in.');
  const board = buildBoard(boardSeed, profile.continent, COLLECT_CONFIG.logicalWidth, logicalHeightFor(mount.clientWidth, mount.clientHeight), { reserves: collectReserves(mount) });
  await startCollectGame(board, mount);
}

/**
 * The space the floating interface takes, converted into the board's units.
 *
 * The overlays are drawn in CSS pixels and the board in logical units, so the same
 * interface is a different number of logical units on a narrow screen than on a
 * wide one. Measuring the boxes and converting is what keeps the field ending
 * exactly where the interface begins; a fixed logical reserve would instead leave
 * a gap of wasted pixels on a wide screen, or slip under the interface on a narrow
 * one. Both boxes are read from the layout that the canvas fills, so their
 * distances from its edges are the distances the board has to keep clear.
 *
 * A missing box or an unmeasurable canvas is a defect in this screen, not a
 * condition to carry on through, so it raises rather than guessing a reserve.
 */
function collectReserves(mount: HTMLElement): BoardReserves {
  const layout = root.querySelector<HTMLElement>('.collect-layout');
  const topRow = root.querySelector<HTMLElement>('.collect-top');
  const readout = root.querySelector<HTMLElement>('.collect-readout');
  if (!layout || !topRow || !readout) {
    throw new Error('Garaimon: the collection screen was painted without its meter row or readout, so the board cannot be told how much space to leave for them.');
  }
  const scale = mount.clientWidth / COLLECT_CONFIG.logicalWidth;
  if (!(scale > 0)) {
    throw new Error('Garaimon: the collection screen has no measurable width, so the board cannot be told how much space to leave for the floating interface.');
  }
  const layoutRect = layout.getBoundingClientRect();
  // Rounded outwards, so a fractional pixel of interface can never end up over a pixel.
  return {
    reservedTop: Math.ceil((topRow.getBoundingClientRect().bottom - layoutRect.top) / scale),
    reservedBottom: Math.ceil((layoutRect.bottom - readout.getBoundingClientRect().top) / scale),
  };
}

/** Paints the mini-game's screen. The canvas itself is filled in by the scene. */
function renderCollectScreen() {
  const name = continentNames[profile.continent];
  collectTargetsPainted = false;
  collectTargetsScrollWired = false;
  collectAimNotice = undefined;
  collectBattleUsed = false;
  resetCollectReels();
  shell(`<section class="collect-layout"><div id="collect-canvas" class="collect-canvas" role="application" tabindex="0" aria-label="Colour field. Grab the pixel and throw it into the floating colours."></div><div class="collect-top"><div class="collect-meter"><span>SEED FILL</span><div class="collect-meter-track"><i id="collect-fill" role="img" aria-label="Colours gathered so far"></i></div><small id="collect-count">0 / 0</small></div><details class="collect-targets"><summary>ENCOUNTER %</summary><div class="collect-targets-panel"><p class="collect-targets-note">Your collected pixels influence the Garaimon you can encounter</p><div class="collect-targets-scroll"><button class="collect-targets-arrow collect-targets-arrow-up" id="collect-targets-up" type="button" aria-label="Scroll up, more Garaimon above" hidden>▲</button><ul id="collect-targets-list"></ul><button class="collect-targets-arrow collect-targets-arrow-down" id="collect-targets-down" type="button" aria-label="Scroll down, more Garaimon below" hidden>▼</button></div></div></details></div><div class="collect-readout"><div class="collect-readout-row"><p class="collect-hint" id="collect-hint">Launch the Seed Pixel to collect colours that determine which Garaimon you can encounter.</p><div class="collect-reels" id="collect-reels" role="list" aria-label="Rarity reel: the leading Garaimon in each rarity" hidden></div><button class="collect-battle" id="collect-battle" type="button" disabled hidden>Battle <span>→</span></button></div>${isDevMode() ? '<button class="collect-dev-sweep" id="collect-sweep" type="button">DEV · SWEEP THIS FIELD</button>' : ''}</div></section>`, `${name.toUpperCase()} · GATHER COLOURS`);
  buildCollectReels();
}

/**
 * Builds the five rarity slots, in ladder order, once per encounter.
 *
 * The slots are the visual answer to "who is in the running", one per rarity rung,
 * so the player can see at a glance that a legendary is being weighed up as well
 * as a common. Order, label and outline colour all come from `RARITY_LADDER`, so
 * the reel can never disagree with the encounter about what the tiers are.
 *
 * Each slot carries the creature's name above its portrait. The tier name is
 * deliberately *not* written in the slot any more: the outline colour and the
 * `aria-label` carry the tier instead, which leaves the slot's whole height for
 * the name and the art.
 */
function buildCollectReels(): void {
  const container = root.querySelector<HTMLElement>('#collect-reels');
  if (!container) return;
  container.innerHTML = RARITY_LADDER.map(
    (rung) =>
      `<div class="collect-reel" data-reel="${rung.tier}" style="--reel-ink:${rung.colour}" role="listitem" title="${rung.label}" aria-label="${rung.label}"><span class="collect-reel-window"><span class="collect-reel-value" data-reel-value>—</span></span><span class="collect-reel-portrait" data-reel-portrait role="img" aria-hidden="true"></span></div>`,
  ).join('');
}

/**
 * Writes one slot: the creature's name and its portrait together.
 *
 * Both are written from one call so a slot can never show one creature's name
 * over another's portrait while it spins or when it lands.
 */
function setReelSlot(tier: RarityTier, entry: { speciesId: string; name: string } | undefined): void {
  const slot = root.querySelector<HTMLElement>(`[data-reel="${tier}"]`);
  if (!slot) return;
  const name = entry?.name ?? '—';
  const value = slot.querySelector<HTMLElement>('[data-reel-value]');
  if (value && value.textContent !== name) value.textContent = name;
  const portrait = slot.querySelector<HTMLElement>('[data-reel-portrait]');
  if (portrait) {
    // Every placeholder creature is drawn in its own colours, built here from the
    // roster, so the same creature is the same colour in the arena as in the slot.
    const path = entry ? reelPortraitUrl(entry.speciesId) : '';
    // Written as a style rather than rebuilt markup, so the spin does not churn
    // the DOM several times a second.
    const image = path ? `url("${path}")` : 'none';
    if (portrait.style.backgroundImage !== image) portrait.style.backgroundImage = image;
  }
}

/** How long each slot cycles its names while spinning, in milliseconds. */
const REEL_SPIN_INTERVAL_MS = 70;
/** How long after the seed settles the first slot lands, in milliseconds. */
const REEL_SETTLE_DELAY_MS = 140;
/**
 * How long each further slot waits before landing, in milliseconds.
 *
 * The slots land in ladder order, common first and legendary last, so the reel
 * reads as climbing toward the rarest answer rather than as five simultaneous
 * flips.
 */
const REEL_SETTLE_STAGGER_MS = 230;
/** How long the landing is allowed to run after the last slot starts, in milliseconds. */
const REEL_SETTLE_TAIL_MS = 260;

/** The reel's own state for the encounter in progress. */
let reelCandidatesByTier = new Map<RarityTier, readonly { speciesId: string; name: string }[]>();
let reelLeaders = new Map<RarityTier, string>();
let reelLanded = false;
let reelRaf = 0;
let reelSpinStartedAt = 0;
let reelSettleStartedAt: number | undefined;

/** Clears the reel for a new encounter: idle, muted, and showing nothing yet. */
function resetCollectReels(): void {
  if (reelRaf) cancelAnimationFrame(reelRaf);
  reelRaf = 0;
  reelLanded = false;
  reelSpinStartedAt = 0;
  reelSettleStartedAt = undefined;
  reelLeaders = new Map();
  reelCandidatesByTier = new Map(
    RARITY_LADDER.map((rung) => [
      rung.tier,
      creaturesOn(profile.continent)
        .filter((creature) => creature.rarity === rung.tier)
        .map((creature) => ({ speciesId: creature.id, name: creature.name })),
    ]),
  );
}

/**
 * Starts the slots spinning, and keeps them turning until the seed settles.
 *
 * Called once at launch. The names cycle while the seed runs and stop the moment
 * the seed comes to rest, so the reel is never still while the outcome can still
 * change: it would otherwise land on a leader that a later bounce overturns.
 */
function startCollectReels(): void {
  if (reelRaf || reelLanded) return;
  reelSpinStartedAt = performance.now();
  reelSettleStartedAt = undefined;
  const tick = () => {
    reelRaf = requestAnimationFrame(tick);
    const now = performance.now();
    for (const rung of RARITY_LADDER) {
      const entries = reelCandidatesByTier.get(rung.tier) ?? [];
      if (entries.length === 0) {
        setReelSlot(rung.tier, undefined);
        continue;
      }
      setReelSlot(rung.tier, entries[Math.floor((now - reelSpinStartedAt) / REEL_SPIN_INTERVAL_MS) % entries.length]!);
    }
    if (reelSettleStartedAt !== undefined) landReels(now);
  };
  reelRaf = requestAnimationFrame(tick);
}

/**
 * Lands the slots, in ladder order, on each tier's leading Garaimon.
 *
 * `reelLeaders` is refreshed by every readout frame from the same ranking the
 * reveal uses, so the slot that lands is the creature the encounter would really
 * produce if that tier were drawn. A tier with no leader (nothing on this
 * continent) settles on a dash rather than being left spinning.
 */
function landReels(now: number): void {
  const started = reelSettleStartedAt!;
  const tail = REEL_SETTLE_DELAY_MS + RARITY_LADDER.length * REEL_SETTLE_STAGGER_MS + REEL_SETTLE_TAIL_MS;
  RARITY_LADDER.forEach((rung, index) => {
    const at = started + REEL_SETTLE_DELAY_MS + index * REEL_SETTLE_STAGGER_MS;
    if (now < at) return;
    const leader = reelLeaders.get(rung.tier);
    setReelSlot(rung.tier, leader ? { speciesId: leader, name: getCreature(leader).name } : undefined);
    const element = root.querySelector<HTMLElement>(`[data-reel="${rung.tier}"]`);
    if (element) element.classList.add('collect-reel-landed');
  });
  if (now - started >= tail) {
    if (reelRaf) cancelAnimationFrame(reelRaf);
    reelRaf = 0;
    reelLanded = true;
  }
}

/**
 * Records each tier's current leader from the readout's candidates.
 *
 * Ties are broken the same way the encounter breaks them, by species id, so a slot
 * cannot name a creature the reveal would not choose if that tier were the one
 * drawn.
 */
function updateCollectReelLeaders(candidates: CollectProgress['candidates']): void {
  const best = new Map<RarityTier, { speciesId: string; probability: number }>();
  for (const candidate of candidates) {
    const held = best.get(candidate.rarity);
    if (!held || candidate.probability > held.probability || (candidate.probability === held.probability && candidate.speciesId < held.speciesId)) {
      best.set(candidate.rarity, { speciesId: candidate.speciesId, probability: candidate.probability });
    }
  }
  reelLeaders = new Map([...best.entries()].map(([tier, entry]) => [tier, entry.speciesId] as const));
}

/**
 * Starts the mini-game and connects the finger to it.
 *
 * One scene instance is created and handed to Phaser, so the screen talks to the
 * same object the game is running rather than looking a scene up by key. Input is
 * wired from `onReady`: before `create()` the scene has no clock, so a finger
 * sample would be stamped with nothing.
 */
async function startCollectGame(board: CollectBoard, mount: HTMLElement) {
  const [phaserModule, collectModule] = await Promise.all([import('phaser'), import('./render/CollectScene')]);
  const PhaserRuntime = phaserModule.default;
  const scene: CollectScene = new collectModule.CollectScene({
    board,
    continent: profile.continent,
    callbacks: {
      onProgress: paintCollectReadout,
      onSettled: (result) => {
        // Held rather than acted on. The board, the composition and the candidate
        // list all stay on screen so the field can be studied, and the Battle
        // button is what moves the encounter on when the player decides to.
        activeBoardResult = result;
      },
      onReady: () => {
        wireCollectInput(mount, scene, board);
        wireCollectSweepButton(board);
        wireCollectBattleButton();
      },
    },
  });
  collectGame = new PhaserRuntime.Game({
    type: PhaserRuntime.CANVAS,
    parent: mount,
    width: board.width,
    height: board.height,
    backgroundColor: '#f3f1e8',
    pixelArt: true,
    antialias: false,
    // Switched off for the same reason as the battle: Phaser's sound manager
    // would otherwise create a second AudioContext with nothing to play through
    // it, and its visibility handling then rejects against the closed context.
    audio: { noAudio: true },
    scale: { mode: PhaserRuntime.Scale.FIT, autoCenter: PhaserRuntime.Scale.CENTER_BOTH },
    scene,
  });
}

/**
 * Forwards finger drags on the board to the scene.
 *
 * Coordinates are converted through the mount's own rectangle, so the same drag
 * works at any screen size and does not depend on the page's zoom. Only the
 * first finger is tracked: a second one landing mid-drag would otherwise split
 * one launch into two conflicting paths.
 */
function wireCollectInput(mount: HTMLElement, scene: CollectScene, board: CollectBoard) {
  let activePointer: number | undefined;
  const toBoard = (event: PointerEvent) => {
    const rect = mount.getBoundingClientRect();
    if (!(rect.width > 0) || !(rect.height > 0)) return { x: 0, y: 0 };
    return { x: ((event.clientX - rect.left) * board.width) / rect.width, y: ((event.clientY - rect.top) * board.height) / rect.height };
  };
  mount.addEventListener('pointerdown', (event) => {
    if (activePointer !== undefined) return;
    const point = toBoard(event);
    // The seed is picked up, not aimed from. A touch that misses it is not a
    // throw, so it is refused with an explanation rather than silently ignored.
    const grabbed = scene.aimBegin(point.x, point.y);
    if (grabbed !== 'grabbed') {
      // 'ignored' means the seed has already been thrown, which the running
      // readout explains on its own; only a real miss needs a message.
      if (grabbed === 'missed') {
        collectAimNotice = 'missed';
        paintCollectHint(undefined);
      }
      return;
    }
    collectAimNotice = undefined;
    activePointer = event.pointerId;
    try {
      mount.setPointerCapture(event.pointerId);
    } catch (error) {
      // Capture is what keeps a drag alive once the finger crosses a readout or
      // leaves the field. If the browser refuses it - the pointer was already
      // released, say - the drag still works while the finger stays on the field,
      // so this is reported rather than allowed to cancel the gesture.
      console.warn('Garaimon: the colour field could not capture the pointer, so a drag that leaves the field will stop.', error);
    }
  });
  mount.addEventListener('pointermove', (event) => {
    if (event.pointerId !== activePointer) return;
    const point = toBoard(event);
    scene.aimMove(point.x, point.y);
  });
  mount.addEventListener('pointerup', (event) => {
    if (event.pointerId !== activePointer) return;
    activePointer = undefined;
    const point = toBoard(event);
    if (scene.aimEnd(point.x, point.y) === 'tap') {
      collectAimNotice = 'tap';
      paintCollectHint(undefined);
    } else {
      collectAimNotice = undefined;
    }
  });
  const abandon = (event: PointerEvent) => {
    if (event.pointerId !== activePointer) return;
    activePointer = undefined;
    scene.aimCancel();
  };
  mount.addEventListener('pointercancel', abandon);
  // A finger that leaves the window never sends `pointerup`. Without this the
  // drag would stay open and the next touch would be read as its continuation.
  mount.addEventListener('lostpointercapture', abandon);
}

/** Whether the pre-launch target list has been painted. Reset for each encounter. */
let collectTargetsPainted = false;

/**
 * The last failed aiming touch to explain, or `undefined` for the normal status
 * line. Reset for each encounter.
 *
 * The readout repaints the hint several times a second while aiming, so a message
 * written once on a miss would be wiped almost immediately. Holding the fact here
 * lets the hint keep saying what went wrong until the player's next touch, which
 * is the only way they can act on it.
 */
let collectAimNotice: 'missed' | 'tap' | undefined;

/**
 * Whether the Battle button has already been pressed for this encounter.
 *
 * The readout repaints several times a second, so without this the button would be
 * re-enabled a tenth of a second after a tap and a double tap could open two
 * reveals. Reset for each encounter, alongside the other per-encounter state.
 */
let collectBattleUsed = false;

/**
 * Runs the launch sweep on the board in front of the player, when developer mode
 * is on.
 *
 * This is the same sweep `npm run collect:sweep` runs, on the same simulation, so
 * a board that looks odd can be measured without rebuilding anything. It is
 * loaded on demand, so a player who never opens it never downloads it.
 *
 * The grid is sampled rather than run whole: a full sweep is a few hundred
 * milliseconds on a laptop and several seconds on a phone, which would freeze the
 * screen. The sample is spread across the whole grid, so it still says whether
 * the board responds to how it is played.
 */
function wireCollectSweepButton(board: CollectBoard) {
  const button = root.querySelector<HTMLButtonElement>('#collect-sweep');
  if (!button) return;
  const hint = () => root.querySelector<HTMLElement>('#collect-hint');
  button.addEventListener('click', async () => {
    button.disabled = true;
    button.textContent = 'SWEEPING…';
    // Painted before the work starts, so the button is never a lie about what is
    // happening, and a frame later the sweep blocks this thread until it is done.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    try {
      const { describeBoardSweep, sweepBoard } = await import('./domain/collectSweep');
      const sweep = sweepBoard(board, { maxShots: 24 });
      for (const line of describeBoardSweep(sweep)) console.log(line);
      const best = sweep.creatures[0];
      const summary = `Sweep: ${sweep.shots.length} launches, ${Math.round(sweep.completionRate * 100)}% complete, spread ${sweep.recipeSpread.toFixed(3)}, closest ${best?.speciesId ?? 'none'} ${best ? Math.round(best.best * 100) : 0}%`;
      const element = hint();
      if (element) element.textContent = summary;
      console.log(summary);
    } catch (error) {
      console.error('Garaimon: the developer sweep could not run.', error);
      const element = hint();
      if (element) element.textContent = 'The sweep could not run. Check the console for details.';
    }
    button.textContent = 'DEV · SWEEP THIS FIELD';
    button.disabled = false;
  });
}

/**
 * Wires the Battle button that appears once the seed has settled.
 *
 * The button is the only route on from the settled board, which is the point:
 * everything the mini-game measured stays readable until the player chooses to
 * move on, rather than the screen deciding for them after a fixed pause.
 */
function wireCollectBattleButton() {
  const button = root.querySelector<HTMLButtonElement>('#collect-battle');
  if (!button) return;
  button.addEventListener('click', () => {
    if (button.disabled) return;
    const result = activeBoardResult;
    if (!result) return;
    // Marked used before the reveal is built, so the readout's next frame cannot
    // re-enable the button and a double tap cannot open two reveals.
    collectBattleUsed = true;
    button.disabled = true;
    try {
      resolveEncounter(result);
    } catch (error) {
      collectBattleUsed = false;
      button.disabled = false;
      reportCollectFailure(error);
    }
  });
}

/**
 * Paints the live readout from one snapshot of the scene.
 *
 * Every part of the readout comes from the same snapshot, so the numbers, the
 * colour bar and the candidate list cannot disagree with each other or with what
 * the simulation actually gathered. Called several times a second, so it only
 * touches text and small lists.
 */
function paintCollectReadout(progress: CollectProgress) {
  const fill = root.querySelector<HTMLElement>('#collect-fill');
  if (fill) {
    fill.style.width = `${Math.round(progress.fill * 100)}%`;
    // The bar's filled part is divided into the colours gathered so far, by their
    // share, so one bar carries both how full the seed is and what it is made of.
    // Shares become flex weights, so the segments are drawn straight from the
    // gathered proportions rather than from a second, rounded copy of them. A
    // floor keeps a colour that has just one pixel from being invisible.
    fill.innerHTML = progress.colours
      .slice(0, 6)
      .map((colour) => `<span style="background:${colour.hex};flex-grow:${Math.max(0.02, colour.share)}" title="${colour.name}"></span>`)
      .join('');
  }
  const count = root.querySelector<HTMLElement>('#collect-count');
  if (count) count.textContent = `${progress.gathered} / ${progress.minimum}`;

  // The slots take the instruction's place: they are hidden while the seed is
  // still being aimed and appear the moment it is launched, which is exactly when
  // `paintCollectHint` hides the instruction. The two never share the band, so
  // the band's height is the taller of the two rather than their sum.
  const reels = root.querySelector<HTMLElement>('#collect-reels');
  if (reels) reels.hidden = progress.phase === 'aiming';

  // The route to the battle appears with the slots, once the seed is launched, and
  // is greyed out until the seed has stopped and the board has settled, so the way
  // on is never a surprise that appears late, and it can never be pressed before
  // there is an outcome to move on to. It is not on screen during the aim: a
  // second element in the band would push the band up over the field's lowest
  // pixels, and while aiming the instruction has the band to itself.
  // `collectBattleUsed` keeps it greyed after it has been pressed, so the readout's
  // own repaint cannot re-enable the button under the player.
  const battle = root.querySelector<HTMLButtonElement>('#collect-battle');
  if (battle) {
    battle.hidden = progress.phase === 'aiming';
    battle.disabled = progress.phase !== 'finished' || collectBattleUsed;
  }

  // Once the seed is moving, a message about a failed grab or tap no longer
  // applies, so the notice is dropped and the ordinary status line returns.
  if (progress.phase !== 'aiming') collectAimNotice = undefined;
  paintCollectHint(progress);

  // The reel is started by the first snapshot that says the seed has been thrown,
  // and it is landed once, when the seed has settled, so a slot never reads an
  // outcome that a later bounce could still overturn.
  if (progress.phase !== 'aiming' && reelSpinStartedAt === 0) startCollectReels();
  updateCollectReelLeaders(progress.candidates);
  if (progress.phase === 'finished' && reelSettleStartedAt === undefined) reelSettleStartedAt = performance.now();

  paintCollectTargets(progress);
  paintCollectTargetMatches(progress);
}

/**
 * Paints the hint line under the composition bar.
 *
 * The aiming readout repaints several times a second, so a message about a failed
 * touch is held in `collectAimNotice` and re-applied here rather than being written
 * once on the event and immediately wiped by the next readout frame.
 */
function paintCollectHint(progress: CollectProgress | undefined): void {
  const hint = root.querySelector<HTMLElement>('#collect-hint');
  if (!hint) return;
  // The lead instruction fills the band while the seed is still being aimed. The
  // moment it is launched the band goes quiet: the run is not narrated, and the
  // space is kept for the candidate-Garaimon portraits that will fill it later.
  // `collectAimNotice` is only ever set while aiming, which is why it stands in
  // for the phase when no snapshot is passed in.
  const aiming = progress ? progress.phase === 'aiming' : collectAimNotice !== undefined;
  hint.classList.toggle('collect-hint-lead', aiming);
  if (collectAimNotice === 'missed') {
    hint.textContent = 'That missed the pixel. Touch the pixel itself, then drag and let go.';
  } else if (collectAimNotice === 'tap') {
    hint.textContent = 'That was a tap. Drag the pixel and let go to throw it.';
  } else {
    hint.textContent = progress ? aimingText(progress) : '';
  }
  // Emptied rather than left as a blank strip of background, so the band is clear.
  hint.hidden = hint.textContent === '';
}

/**
 * What the band says, which is only ever the aiming instruction.
 *
 * After launch there is no line: the player watches the seed run rather than read
 * about it, and the settled board is explained by its own meters and by the Battle
 * button. Keeping this a function of the phase, rather than of the gathered
 * numbers, is what makes the band go quiet the instant the seed is thrown.
 */
function aimingText(progress: CollectProgress): string {
  return progress.phase === 'aiming' ? 'Launch the Seed Pixel to collect colours that determine which Garaimon you can encounter.' : '';
}

/**
 * Paints what this continent's creatures are made of, once.
 *
 * This is the pre-launch information that makes aiming deliberate. It does not
 * change during an attempt, so it is painted on the first snapshot and then left
 * alone. The one thing on each row that does change - how close the collection is
 * to that creature - is written by `paintCollectTargetMatches` instead, so the
 * rows are not rebuilt ten times a second.
 *
 * Each row also shows how much of that creature's colours this board carries. A
 * board holds only a handful of the palette's colours, so on some boards a
 * creature has almost none of its own colours present and no launch could gather
 * for it; saying so is the difference between offering five targets and offering
 * five names. The number is colours on the field, not a chance of appearing, and
 * a target with almost nothing present is dimmed rather than hidden - it can
 * still be the one that comes out.
 */
function paintCollectTargets(progress: CollectProgress) {
  if (collectTargetsPainted || progress.targets.length === 0) return;
  const list = root.querySelector<HTMLElement>('#collect-targets-list');
  if (!list) return;
  collectTargetsPainted = true;
  list.innerHTML = progress.targets.map((target) => {
    const creature = getCreature(target.speciesId);
    const swatches = target.hexes.map((hex) => `<i style="background:${hex}"></i>`).join('');
    const presence = Math.round(target.boardPresence * 100);
    const thin = target.boardPresence < BARELY_PRESENT ? ' collect-target-thin' : '';
    return `<li class="collect-target${thin}"><span class="collect-swatches">${swatches}</span><span class="collect-target-name"><b>${creature.name}</b><i class="collect-presence" role="img" aria-label="${presence}% of its colours are on this field"><u style="width:${presence}%"></u></i><small class="collect-match" data-match="${target.speciesId}">not launched yet</small></span></li>`;
  }).join('');
  wireCollectScrollArrows();
  updateCollectScrollArrows();
}

/** Whether the list's scroll listener and arrows have been wired for this screen. */
let collectTargetsScrollWired = false;

/**
 * Shows each arrow only while there is more of the list in that direction.
 *
 * The strips are always in the DOM and only their visibility changes, so their
 * position never shifts the rows. A pixel of slack at each end absorbs the
 * fractional `scrollTop` a browser can report at the very top or bottom, which
 * would otherwise make an arrow flicker under the thumb.
 */
function updateCollectScrollArrows(): void {
  const list = root.querySelector<HTMLElement>('#collect-targets-list');
  const up = root.querySelector<HTMLButtonElement>('#collect-targets-up');
  const down = root.querySelector<HTMLButtonElement>('#collect-targets-down');
  if (!list || !up || !down) return;
  const overflow = list.scrollHeight - list.clientHeight;
  up.hidden = list.scrollTop <= 1;
  down.hidden = overflow <= 1 || list.scrollTop >= overflow - 1;
}

/**
 * Wires the list's scroll listener and the two arrows, once per screen.
 *
 * The arrows scroll by one row so a tap moves the list a predictable amount
 * instead of a screenful. Recomputing on the panel's own `toggle` matters because
 * a closed `<details>` has no height, so the arrows can only be judged once it is
 * open.
 */
function wireCollectScrollArrows(): void {
  if (collectTargetsScrollWired) return;
  const list = root.querySelector<HTMLElement>('#collect-targets-list');
  if (!list) return;
  const panel = root.querySelector<HTMLElement>('.collect-targets');
  if (!panel) return;
  collectTargetsScrollWired = true;
  list.addEventListener('scroll', updateCollectScrollArrows, { passive: true });
  panel.addEventListener('toggle', updateCollectScrollArrows);
  const scrollByRow = (direction: 1 | -1) => () => {
    const row = list.querySelector('li');
    const step = row ? row.getBoundingClientRect().height : 0;
    list.scrollBy({ top: direction * Math.max(24, step), behavior: 'smooth' });
  };
  root.querySelector<HTMLButtonElement>('#collect-targets-up')?.addEventListener('click', scrollByRow(-1));
  root.querySelector<HTMLButtonElement>('#collect-targets-down')?.addEventListener('click', scrollByRow(1));
}

/**
 * Writes the real encounter chance into each creature's row.
 *
 * This is the chance the reveal will actually use - the rarity-weighted, match-
 * scaled, any-colour-opened share - rather than the raw colour match. Before the
 * seed is thrown nothing has been gathered and no any-colour pixel is held, so
 * every row reads 0%, which is the honest reading of a board the player has not
 * played yet: the chance is earned in the mini-game, not granted by opening it.
 *
 * The figure is written under an "ENCOUNTER %" header, so the row does not repeat
 * "chance" on every line. Percentages below one are given a decimal, because a
 * legendary's real chance can be a tenth of a percent and rounding it to "0%"
 * would be indistinguishable from having no chance at all.
 */
function paintCollectTargetMatches(progress: CollectProgress) {
  const chances = new Map(progress.candidates.map((candidate) => [candidate.speciesId, candidate.probability]));
  root.querySelectorAll<HTMLElement>('[data-match]').forEach((element) => {
    const chance = progress.phase === 'aiming' ? 0 : (chances.get(element.dataset.match ?? '') ?? 0);
    const next = formatChance(chance);
    // Only touched when the figure actually moves, so the row is not rewritten
    // ten times a second for no reason.
    if (element.textContent !== next) element.textContent = next;
    element.classList.toggle('collect-match-close', chance >= CHANCE_HIGHLIGHT);
  });
}

/** The chance at or above which a row is marked as a leading contender. */
const CHANCE_HIGHLIGHT = 0.2;

/**
 * A chance as a percentage, with enough precision to tell a small chance from none.
 *
 * Whole percents above 10, one decimal down to 1%, two decimals below that. The
 * point is that a legendary's 0.1% never renders as "0%", which is the one
 * reading that would be wrong rather than merely imprecise.
 */
function formatChance(chance: number): string {
  const percent = chance * 100;
  if (percent <= 0) return '0%';
  if (percent >= 10) return `${Math.round(percent)}%`;
  if (percent >= 1) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(2)}%`;
}

/**
 * The presence below which a target is dimmed.
 *
 * A third. Below it, two of every three colours the creature is made of are not
 * on the field at all, which is a fact worth showing plainly rather than a
 * judgement that the creature cannot appear.
 */
const BARELY_PRESENT = 0.34;

/**
 * Reports a failure to open the mini-game in the page, not only in the console,
 * and returns to the map so the player is not left on a dead screen.
 */
function reportCollectFailure(error: unknown) {
  console.error('Garaimon: the colour collection could not start.', error);
  teardownCollectGame();
  renderHome();
  const banner = document.createElement('p');
  banner.className = 'hint';
  banner.setAttribute('role', 'alert');
  banner.textContent = 'The colour collection could not start. Check the console for details.';
  root.querySelector('.world-map')?.insertAdjacentElement('beforebegin', banner);
}

/**
 * Turns a finished collection into an encounter.
 *
 * The creature is chosen by the same colour matching the readout was previewing,
 * so the name the player watched climb that list is the one that appears - and the
 * chance is the one the panel showed, any-colour pixels included. The draw is
 * seeded from the board seed, which makes a reveal reproducible from the collection
 * and the seed alone. The arena is rolled from its own seed, so the ground the
 * battle happens on never depends on what was collected.
 */
function resolveEncounter(result: CollectResult) {
  const selection = selectEncounter(result.mixture, profile.continent, seededRandom(boardSeed ^ 0x2f1a37), result.anyColourPixels);
  candidateId = selection.chosenSpeciesId;
  activeArena = rollArena(arenaSeed, getCreature(candidateId).element);
  profile.recentBoardSeeds = [result.boardSeed, ...profile.recentBoardSeeds].slice(0, 8);
  void save();
  showEncounterReveal();
}

/** How long each beat of the reveal runs, in milliseconds. */
const REVEAL_CHARGE_MS = 760;
const REVEAL_FLASH_MS = 220;
const REVEAL_NAME_MS = 380;
const REVEAL_HOLD_MS = 1200;
const REVEAL_WIPE_MS = 560;

/** How many columns the arena wipe is divided into. More columns means finer pixels. */
const WIPE_COLUMNS = 14;

/** Timers for the reveal in flight, so a developer re-roll cancels the old sequence. */
let revealTimers: number[] = [];

function clearRevealTimers() {
  for (const timer of revealTimers) clearTimeout(timer);
  revealTimers = [];
}

/**
 * Plays the reveal over the settled board, then wipes into the arena.
 *
 * This is a DOM overlay rather than a Phaser scene because the mini-game is torn
 * down at the end of it and the battle scene is built after it: the reveal exists
 * in the gap between two Phaser games, so it cannot belong to either.
 *
 * The sequence is: the seed shown as its gathered colour bands, pixel motes
 * streaming into it, a white flash, then the Garaimon and its name, and finally a
 * column-by-column wipe in the arena's own dark green. Every step is CSS driven
 * so the animation cannot drift from the timings that decide when the battle
 * starts.
 */
function showEncounterReveal() {
  clearRevealTimers();
  const creature = getCreature(candidateId);
  const recipe = activeBoardResult ? resultToRecipe(activeBoardResult) : undefined;

  const bands = recipe && recipe.colours.length > 0
    ? recipe.colours
        .slice(0, 8)
        .map((entry, index) => `<i style="--b:${index};background:${entry.hex};flex-grow:${Math.max(0.02, entry.share)}"></i>`)
        .join('')
    : '';
  // Motes sit on a ring and stream inwards; the stagger is what makes them read
  // as a stream of pixels rather than one pulse.
  const motes = Array.from({ length: 12 }, (_, index) => {
    const angle = (index / 12) * Math.PI * 2;
    const radius = 34 + (index % 3) * 9;
    const x = Math.round(Math.cos(angle) * radius);
    const y = Math.round(Math.sin(angle) * radius);
    return `<b style="--x:${x}px;--y:${y}px;--m:${index}"></b>`;
  }).join('');
  const wipe = Array.from({ length: WIPE_COLUMNS }, (_, column) => `<i style="--col:${column}"></i>`).join('');

  root.querySelector('#reveal-overlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'reveal-overlay';
  overlay.className = 'reveal-overlay is-charging';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', `A wild ${creature.name} appeared`);
  overlay.innerHTML =
    `<div class="reveal-wipe" aria-hidden="true">${wipe}</div>` +
    `<div class="reveal-stage"><span class="tag">WILD GARAIMON SPOTTED</span>` +
    `<div class="reveal-seed" aria-hidden="true"><div class="reveal-seed-bands">${bands}</div><div class="reveal-motes">${motes}</div></div>` +
    `<div class="reveal-creature" style="--creature:${color(creature.color)};--accent:${color(creature.accent)}"><span>✦</span><i>◡</i></div>` +
    `<h1 class="reveal-name">${creature.name}</h1><p class="reveal-sub">${creature.habitat} · ${creature.element} type</p>` +
    `${revealRecipeMarkup(activeBoardResult)}` +
    `<p class="reveal-arena">◈ ${activeArena?.cover ?? '-'} cover · ↗ ${activeArena?.openLanes ?? '-'} open lanes · ✧ ${activeArena?.affinity ?? '-'} affinity</p>` +
    `${devOpponentSelectMarkup()}</div>`;
  root.querySelector('.app-shell')?.append(overlay);

  // A developer can re-roll the opponent and watch the reveal again, which is
  // why the sequence is rebuilt from scratch rather than patched in place.
  overlay.querySelector<HTMLSelectElement>('#dev-opponent')?.addEventListener('change', (event) => {
    candidateId = (event.target as HTMLSelectElement).value;
    activeArena = rollArena(arenaSeed, getCreature(candidateId).element);
    showEncounterReveal();
  });

  const after = (ms: number, action: () => void) => revealTimers.push(window.setTimeout(action, ms));
  after(REVEAL_CHARGE_MS, () => overlay.classList.add('is-flash'));
  after(REVEAL_CHARGE_MS + REVEAL_FLASH_MS, () => overlay.classList.add('is-revealed'));
  const wipeAt = REVEAL_CHARGE_MS + REVEAL_FLASH_MS + REVEAL_NAME_MS + REVEAL_HOLD_MS;
  after(wipeAt, () => overlay.classList.add('is-wipe'));
  after(wipeAt + REVEAL_WIPE_MS, () => {
    clearRevealTimers();
    overlay.remove();
    // The mini-game goes only now: the settled board had to stay painted behind
    // the reveal, and the battle screen that replaces it is built next.
    teardownCollectGame();
    void startBattle().catch(reportBattleFailure);
  });
}

/**
 * The colours the collection was made of, as a strip, with how many pixels were
 * gathered against how many were needed.
 *
 * Shown on the reveal so it still says what the collection was made of rather
 * than only which creature it produced. Absent when there is no collection to
 * show, which is the case for a battle resumed from a save.
 */
function revealRecipeMarkup(result: CollectResult | undefined): string {
  if (!result) return '';
  const recipe = resultToRecipe(result);
  if (recipe.colours.length === 0) return '';
  const swatches = recipe.colours.slice(0, 6).map((entry) => `<i style="background:${entry.hex};flex-grow:${Math.max(0.02, entry.share)}" title="${entry.anchorId}"></i>`).join('');
  const met = recipe.totalCollected >= recipe.minimumRequired;
  return `<div class="reveal-recipe"><span class="collect-composition">${swatches}</span><small>${recipe.totalCollected} pixels gathered · ${met ? 'target met' : `${recipe.minimumRequired} needed`}</small></div>`;
}

function color(n:number){return `#${n.toString(16).padStart(6,'0')}`;}

/**
 * The GachaCard, drawn as the SD card from the wordmark.
 *
 * The design is taken from the brand logo, where an SD card is drawn as the "O" of
 * GARAIMON. Measured off that art, the card is 131 wide to 155 tall, its border is
 * about an eighth of its width, its corners are stepped rather than square, and
 * three bars fill the top third of the inside. Those four things are what make the
 * shape recognisable, so they are what is reproduced; the bars and the stepping are
 * the details that survive being drawn small.
 *
 * The creature's own colour fills the card so a player can still tell which
 * Garaimon they are holding, which is why this is not simply the logo's pink.
 */
function cartridgeMarkup(name:string, speciesId:string) {
  const creature = getCreature(speciesId);
  const shellColor = color(creature.color);
  return `<svg class="cartridge-art" viewBox="0 0 96 112" aria-hidden="true" shape-rendering="crispEdges"><path d="M20 2H76V6H82V10H86V102H82V106H76V110H20V106H14V102H10V10H14V6H20Z" fill="#18212d"/><path d="M26 10H70V13H74V16H78V96H74V99H70V102H26V99H22V96H18V16H22V13H26Z" fill="${shellColor}"/><rect x="26" y="16" width="9" height="27" fill="#fff0c8"/><rect x="44" y="16" width="9" height="27" fill="#fff0c8"/><rect x="62" y="16" width="9" height="27" fill="#fff0c8"/><text x="48" y="82" text-anchor="middle" fill="#fff0c8" font-size="10" font-family="monospace" font-weight="bold">${name.slice(0,13).toUpperCase()}</text></svg>`;
}

/**
 * The name shown for a battle partner. A temporary developer-mode partner has no
 * nickname, so it falls back to the species name.
 */
function partnerDisplayName(partner: BattlePartner): string {
  return partner.instance?.nickname || getCreature(partner.speciesId).name;
}

/**
 * The AI profile for a species fielded through developer mode. There is no saved
 * instance to read a profile from, so one is generated for the style; the
 * `?devStyle` override still wins, so any species can be replayed under any style.
 */
function devPartnerProfile(speciesId: string): AIProfile {
  return makeAIProfile(forcedStyle ?? getCreature(speciesId).style, (activeArena?.seed ?? 0) ^ 35);
}

/**
 * One selectable partner, identified by the value carried on its control.
 *
 * The battle screen offers partners in two places: the roster panel and, with
 * developer mode on, the dropdown. Both render from this one list, so the two
 * cannot disagree about what is selectable.
 */
interface PartnerOption {
  /** An owned instance id or a `dev:`-prefixed species id. */
  value: string;
  speciesId: string;
  name: string;
  detail: string;
  /** Whether the account owns the creature, or it is fielded for testing. */
  owned: boolean;
}

/**
 * The partner list, normal play first. With developer mode on, every species the
 * account does not own is appended and marked DEV, so any creature can be
 * fielded for testing without being added to the roster.
 */
function partnerOptions(): PartnerOption[] {
  const owned: PartnerOption[] = profile.garaimon.map((instance) => {
    const creature = getCreature(instance.speciesId);
    return { value: instance.id, speciesId: instance.speciesId, name: instance.nickname || creature.name, detail: `${creature.name} · Lv. ${instance.level}`, owned: true };
  });
  if (!isDevMode()) return owned;
  const ownedSpecies = new Set(profile.garaimon.map((instance) => instance.speciesId));
  const testing: PartnerOption[] = roster.filter((creature) => !ownedSpecies.has(creature.id)).map((creature) => ({
    value: `${DEV_SUMMON_PREFIX}${creature.id}`,
    speciesId: creature.id,
    name: creature.name,
    detail: `${continentNames[creature.continent]} · ${styleLabels[creature.style]}`,
    owned: false,
  }));
  return [...owned, ...testing];
}

/** The value identifying the partner currently fielded, matching a `PartnerOption`. */
function activePartnerValue(): string {
  if (!activeBattlePartner) return '';
  return activeBattlePartner.instance?.id ?? `${DEV_SUMMON_PREFIX}${activeBattlePartner.speciesId}`;
}

/** The partner panel, opened by the summon card. */
function rosterChoiceMarkup(chosenValue: string): string {
  const optionMarkup = (option: PartnerOption): string => {
    const chosen = option.value === chosenValue;
    const creature = getCreature(option.speciesId);
    const art = `<span class="roster-orb" style="--creature:${color(creature.color)};--accent:${color(creature.accent)}">✦</span>`;
    const tag = chosen ? 'READY' : option.owned ? 'PICK' : 'DEV';
    return `<button class="roster-choice${chosen ? ' chosen' : ''}" data-summon="${option.value}">${art}<span><b>${option.name}</b><small>${option.detail}</small></span><i>${tag}</i></button>`;
  };
  const options = partnerOptions();
  const owned = options.filter((option) => option.owned).map(optionMarkup).join('');
  const testing = options.filter((option) => !option.owned).map(optionMarkup).join('');
  return testing ? `${owned}<p class="roster-dev-note">DEV · FOR TESTING, NOT ADDED TO YOUR ROSTER</p>${testing}` : owned;
}

/**
 * The partner picker that sits on the battle screen while developer mode is on.
 *
 * The summon card is the normal way to choose a partner, but it is hidden for the
 * rest of the battle once it has been thrown, which leaves the partner panel with
 * no way to be reopened. This dropdown is an always-available route to the same
 * choice, so the partner can be changed at any point, including after summoning.
 */
function devPartnerSelectMarkup(chosenValue: string): string {
  if (!isDevMode()) return '';
  const options = partnerOptions().map((option) => `<option value="${option.value}"${option.value === chosenValue ? ' selected' : ''}>${option.name}${option.owned ? '' : ' · DEV'}</option>`).join('');
  return `<label class="dev-partner" for="dev-partner"><span>DEV · CHANGE PARTNER</span><select id="dev-partner">${options}</select></label>`;
}

/**
 * Fields the chosen partner and brings every view of it up to date.
 *
 * Both the roster panel and the developer dropdown route through here, so the
 * health card, the summon label, the card art and the other picker can never be
 * left showing a partner that is no longer the one in the arena.
 */
function applyPartnerChoice(value: string, scene: BattleScene): void {
  let shownName = '';
  let speciesId = '';
  if (value.startsWith(DEV_SUMMON_PREFIX)) {
    const devSpeciesId = value.slice(DEV_SUMMON_PREFIX.length);
    const testingProfile = devPartnerProfile(devSpeciesId);
    activeBattlePartner = { speciesId: devSpeciesId, profile: testingProfile };
    scene.selectGaraimon(devSpeciesId, testingProfile);
    shownName = getCreature(devSpeciesId).name;
    speciesId = devSpeciesId;
  } else {
    const instance = profile.garaimon.find((entry) => entry.id === value);
    if (!instance) return;
    const nextProfile = partnerProfile(instance) ?? instance.aiProfile;
    activeBattlePartner = { speciesId: instance.speciesId, profile: nextProfile, instance };
    scene.selectGaraimon(instance.speciesId, nextProfile);
    const creature = getCreature(instance.speciesId);
    shownName = instance.nickname || creature.name;
    speciesId = instance.speciesId;
  }
  const cardLabel = root.querySelector('.fighter-card.yours b');
  if (cardLabel) cardLabel.textContent = shownName;
  const trayLabel = root.querySelector('.summon-label b');
  if (trayLabel) trayLabel.textContent = shownName;
  root.querySelector('#summon-card .cartridge-art')?.remove();
  root.querySelector('#summon-card')?.insertAdjacentHTML('afterbegin', cartridgeMarkup(shownName, speciesId));
  const select = root.querySelector<HTMLSelectElement>('#dev-partner');
  if (select) select.value = value;
  root.querySelectorAll<HTMLElement>('.roster-choice').forEach((item) => item.classList.toggle('chosen', item.dataset.summon === value));
  root.querySelector('#roster-overlay')?.classList.add('hidden');
}

/**
 * The opponent picker for the reveal, or an empty string when developer mode is
 * off. Grouped by continent so a specific species is quick to find. Choosing one
 * replaces the wild Garaimon the encounter would have rolled, and replays the
 * reveal for it.
 */
function devOpponentSelectMarkup(): string {
  if (!isDevMode()) return '';
  const groups = (Object.keys(continentNames) as ContinentId[]).map((continent) => {
    const options = creaturesOn(continent).map((creature) => `<option value="${creature.id}"${creature.id===candidateId?' selected':''}>${creature.name} · ${styleLabels[creature.style]}</option>`).join('');
    return `<optgroup label="${continentNames[continent]}">${options}</optgroup>`;
  }).join('');
  return `<label class="dev-field" for="dev-opponent"><span>DEV · CHOOSE OPPONENT</span><select id="dev-opponent">${groups}</select></label>`;
}

function reportBattleFailure(error: unknown) {
  console.error('Garaimon: the battle screen failed to finish setting up, so some controls are unavailable.', error);
  // The music is stopped here as well as when a battle ends normally. If setting
  // the controls up threw, the scene's update handler may never be wired, so the
  // normal end-of-battle hook would never run and the music would play on with no
  // battle in front of it.
  stopBattleMusic();
  const banner = document.createElement('p');
  banner.className = 'hint';
  banner.setAttribute('role', 'alert');
  banner.textContent = 'Battle controls failed to load. Check the console for details.';
  root.querySelector('.battle-layout')?.append(banner);
}

async function startBattle() {
  // Audio is unlocked synchronously, before this function's first await. The
  // Phaser import below is that await, and once it has run the tap that opened
  // this screen is over, so the browser would no longer allow sound to start.
  unlockAudio();
  // The id doubles as this battle's token: if a later await finishes after the
  // player has already retreated, the token no longer matches and the arena is
  // not built into a container that has been replaced.
  const battleId = crypto.randomUUID();
  activeBattleId = battleId;
  battleResultHandled = false;
  battleInProgress = true;
  activeBattleScene = undefined;
  retreatDestination = undefined;
  const musicTrack = forcedMusicTrack ?? pickRandomBattleTrack();
  // Loading starts now so the download overlaps the arena transition instead of
  // holding up the first note.
  void preloadBattleMusic(musicTrack).catch((error) => console.warn('Garaimon: battle music could not load.', error));
  // The starter is granted by an explicit, named step rather than as a silent
  // side effect of starting a battle.
  if (grantStarterGaraimon(profile, profile.continent, candidateId)) void save();
  const firstPartner = profile.garaimon[0];
  activeBattlePartner = firstPartner ? { speciesId: firstPartner.speciesId, profile: partnerProfile(firstPartner) ?? firstPartner.aiProfile, instance: firstPartner } : undefined;
  const opponentName = getCreature(candidateId).name;
  const partnerName = activeBattlePartner ? partnerDisplayName(activeBattlePartner) : getCreature(FALLBACK_SPECIES_ID).name;
  const partnerSpeciesId = activeBattlePartner?.speciesId ?? FALLBACK_SPECIES_ID;
  const partnerValue = activePartnerValue();
  const choices = rosterChoiceMarkup(partnerValue);
  shell(`<section class="battle-layout"><div id="battle-canvas" class="battle-canvas"><div class="arena-transition"><div class="transition-fragments">${Array.from({length:16},(_,i)=>`<i style="--i:${i}"></i>`).join('')}</div><span>SYNCING THE ARENA</span></div></div><div class="battle-headline"><span>${continentNames[profile.continent].toUpperCase()} · WILD ENCOUNTER</span><b>${opponentName}</b></div><div class="battle-hud"><div class="fighter-card"><b>${opponentName}</b><div class="hp-track"><i id="enemy-hp"></i></div><small>WILD · LV 1</small></div><div class="battle-clock"><b id="battle-time">0:00</b></div><div class="fighter-card yours"><b>${partnerName}</b><div class="hp-track"><i id="player-hp"></i></div><small>YOUR PARTNER · LV 1</small></div></div>${devPartnerSelectMarkup(partnerValue)}<div class="music-controls" aria-label="Battle music"><button id="music-toggle" type="button" aria-label="Mute music" aria-pressed="false">♫</button></div><div class="zoom-controls" aria-label="Arena camera controls"><button id="zoom-out" aria-label="Zoom out">−</button><button id="zoom-reset" aria-label="Reset zoom">⌂</button><button id="zoom-in" aria-label="Zoom in">+</button></div><div class="camera-hint">PINCH TO ZOOM · DRAG TO LOOK</div><div class="summon-tray" id="summon-tray"><button class="sd-card" id="summon-card" aria-label="Choose Garaimon or swipe the GachaCard into the arena">${cartridgeMarkup(partnerName, partnerSpeciesId)}<span class="summon-label"><b>${partnerName}</b><small>Tap to choose<br>Swipe to summon</small></span></button></div><div class="battle-actions"><button id="catch-mode" class="catch-button" disabled>◎ <span>Catch</span><small>${profile.gachaCards} CARD${profile.gachaCards===1?'':'S'}</small></button><button id="give-up" class="give-up">Retreat</button></div><div id="catch-overlay" class="catch-overlay hidden"><div class="catch-modal"><span class="step">GACHACARD ACTIVE</span><h2>Get close!</h2><p>Steer your Garaimon near the wild one to fill the catch meter.</p><div class="catch-meter"><i id="catch-progress"></i></div><small id="catch-status">Use the stick to close in</small><div class="joystick" id="joystick"><div class="joy-knob"></div></div><button class="text-button" id="cancel-catch">Cancel catch</button></div></div><div id="roster-overlay" class="roster-overlay hidden"><div class="roster-modal"><span class="step">YOUR TEAM</span><h2>Choose your Garaimon</h2><div class="roster-choices">${choices}</div><button class="text-button" id="close-roster">Back to battle</button></div></div></section>`, 'AUTOBATTLE');
  const [phaserModule, sceneModule] = await Promise.all([import('phaser'), import('./render/BattleScene')]);
  // A retreat confirmed during the arena transition leaves this battle behind.
  // Without this check the game would still be created for a screen that is gone.
  if (!battleInProgress || activeBattleId !== battleId) return;
  const PhaserRuntime = phaserModule.default;
  const scene = new sceneModule.BattleScene(candidateId, partnerSpeciesId, activeArena!, activeBattlePartner?.profile);
  const arenaRect = root.querySelector('#battle-canvas')!.getBoundingClientRect();
  const worldHeight = Math.round(360 * arenaRect.height / arenaRect.width);
  // Assigned before the game is constructed. Phaser defers `create()` until its
  // textures are ready, but assigning first removes any ordering assumption.
  scene.onReady = () => {
    try {
      // A retreat can land between the game being created and its textures being
      // ready. Checking here is what stops the music and the controls from being
      // wired into a battle the player has already left.
      if (!battleInProgress || activeBattleId !== battleId) return;
      wireBattleControls(scene, worldHeight, musicTrack);
      // Set only once the controls exist, so a retreat during loading takes the
      // direct teardown path rather than calling into a half-built scene.
      activeBattleScene = scene;
    } catch (error) {
      reportBattleFailure(error);
    }
  };
  // Phaser's sound system is switched off, because this game plays no sound
  // effects and battle music is handled by `src/platform/battleMusic.ts`.
  //
  // Left on, Phaser creates its own `AudioContext`, and that one does damage to
  // the page. Its sound manager closes that context when the battle game is
  // destroyed, but its `onGameVisible` handler still calls `suspend()` and
  // `resume()` on a timer and ignores the promises those return. Once the
  // context has been closed, each call rejects with "Cannot suspend a closed
  // AudioContext", which shows up as an unhandled promise rejection in the
  // console after every battle and would hide real errors. With no audio there
  // is no second context to close, so the problem cannot occur.
  battleGame = new PhaserRuntime.Game({type:PhaserRuntime.CANVAS,parent:'battle-canvas',width:360,height:worldHeight,backgroundColor:'#9edb83',pixelArt:true,antialias:false,audio:{noAudio:true},scale:{mode:PhaserRuntime.Scale.FIT,autoCenter:PhaserRuntime.Scale.CENTER_BOTH},scene});
}

/**
 * Starts the battle's music and wires the on-screen mute toggle.
 *
 * Both live here so the button and the sound cannot disagree: the button is
 * drawn from the manager's own mute state rather than from a local copy, and
 * every tap re-reads it.
 *
 * The tap itself is also a chance to unlock audio. If the browser suspended the
 * audio clock while the game was in the background, toggling mute is a user
 * gesture that can bring it back.
 */
function wireBattleMusic(track: BattleMusicTrack) {
  const button = root.querySelector<HTMLButtonElement>('#music-toggle');
  const paint = () => {
    if (!button) return;
    const muted = isMusicMuted();
    button.textContent = muted ? '♪' : '♫';
    button.setAttribute('aria-pressed', String(muted));
    button.setAttribute('aria-label', muted ? 'Unmute music' : 'Mute music');
  };
  paint();
  button?.addEventListener('click', () => {
    toggleMusicMuted();
    unlockAudio();
    paint();
  });
  void playBattleMusic(track).catch((error) => console.warn('Garaimon: battle music could not start.', error));
}

/**
 * Wires every on-screen battle control. Runs once the Phaser scene has finished
 * creating itself, which is the earliest point at which `scene.cameras` and the
 * fighter objects exist. A failure here is reported rather than silently leaving
 * the screen inert.
 */
function wireBattleControls(scene: BattleScene, worldHeight: number, musicTrack: BattleMusicTrack) {
  root.querySelector('.arena-transition')?.remove();
  wireBattleMusic(musicTrack);
  const camera=scene.cameras.main;
  const cameraBounds=arenaCameraBounds(360,worldHeight);
  camera.setBounds(cameraBounds.x,cameraBounds.y,cameraBounds.width,cameraBounds.height);
  // Phaser applies its own bounds during rendering, and that clamp pins the view
  // to the edge of the bounds once the zoomed-out view is bigger than them, which
  // would push the arena into a corner and clip the far edge. `clampCamera` below
  // replaces it so the view stays centred instead.
  camera.useBounds=false;
  const arena=root.querySelector<HTMLElement>('#battle-canvas')!;
  const activePointers=new Map<number,{x:number;y:number}>();
  let lastPan:{x:number;y:number}|undefined;
  let lastPinch=0;
  /**
   * Keeps one axis of the camera inside the padded arena bounds, and returns the
   * corrected scroll for that axis.
   *
   * Phaser measures scroll from the camera's centre, not from the corner of the
   * view: the leading edge of the view sits at `scroll + (halfView - view / 2)`.
   * Clamping the scroll value directly is therefore wrong by that offset, which
   * is what pushed the arena into the bottom-right corner instead of centring it.
   *
   * When the zoomed-out view is larger than the bounds there is nothing left to
   * pan, so the bounds are centred in the view instead of pinned to an edge.
   */
  const clampAxis = (scroll: number, view: number, boundStart: number, boundSize: number, halfView: number): number => {
    if (view >= boundSize) return boundStart + boundSize / 2 - halfView;
    const leadingEdge = scroll + (halfView - view / 2);
    const clampedEdge = Math.max(boundStart, Math.min(boundStart + boundSize - view, leadingEdge));
    return clampedEdge - (halfView - view / 2);
  };
  const clampCamera=()=>{camera.scrollX=clampAxis(camera.scrollX,camera.width/camera.zoom,cameraBounds.x,cameraBounds.width,camera.width/2);camera.scrollY=clampAxis(camera.scrollY,camera.height/camera.zoom,cameraBounds.y,cameraBounds.height,camera.height/2);};
  const zoomAt=(next:number,x:number,y:number)=>{const rect=arena.getBoundingClientRect(),px=(x-rect.left)*360/rect.width,py=(y-rect.top)*worldHeight/rect.height;const world=camera.getWorldPoint(px,py);camera.setZoom(Math.max(MIN_ZOOM,Math.min(MAX_ZOOM,next)));const after=camera.getWorldPoint(px,py);camera.scrollX+=world.x-after.x;camera.scrollY+=world.y-after.y;clampCamera();const reset=root.querySelector<HTMLButtonElement>('#zoom-reset');if(reset)reset.textContent=`${camera.zoom.toFixed(1)}×`;};
  arena.addEventListener('pointerdown',(event)=>{if((event.target as HTMLElement).closest('button,.summon-tray,.battle-actions,.zoom-controls,.catch-overlay,.roster-overlay'))return;activePointers.set(event.pointerId,{x:event.clientX,y:event.clientY});arena.setPointerCapture(event.pointerId);if(activePointers.size===1)lastPan={x:event.clientX,y:event.clientY};if(activePointers.size===2){const points=[...activePointers.values()];lastPinch=Math.hypot(points[1]!.x-points[0]!.x,points[1]!.y-points[0]!.y);lastPan=undefined;}});
  arena.addEventListener('pointermove',(event)=>{if(!activePointers.has(event.pointerId))return;activePointers.set(event.pointerId,{x:event.clientX,y:event.clientY});const points=[...activePointers.values()];if(points.length>=2){const distance=Math.hypot(points[1]!.x-points[0]!.x,points[1]!.y-points[0]!.y),midX=(points[0]!.x+points[1]!.x)/2,midY=(points[0]!.y+points[1]!.y)/2;if(lastPinch)zoomAt(camera.zoom*distance/lastPinch,midX,midY);lastPinch=distance;}else if(lastPan){camera.scrollX-=(event.clientX-lastPan.x)/camera.zoom;camera.scrollY-=(event.clientY-lastPan.y)/camera.zoom;lastPan={x:event.clientX,y:event.clientY};clampCamera();}});
  const endPointer=(event:PointerEvent)=>{activePointers.delete(event.pointerId);if(activePointers.size<2)lastPinch=0;lastPan=activePointers.size===1?[...activePointers.values()][0]:undefined;};
  arena.addEventListener('pointerup',endPointer);arena.addEventListener('pointercancel',endPointer);
  root.querySelector('#zoom-in')?.addEventListener('click',()=>zoomAt(camera.zoom+0.25,arena.getBoundingClientRect().left+arena.clientWidth/2,arena.getBoundingClientRect().top+arena.clientHeight/2));
  root.querySelector('#zoom-out')?.addEventListener('click',()=>zoomAt(camera.zoom-0.25,arena.getBoundingClientRect().left+arena.clientWidth/2,arena.getBoundingClientRect().top+arena.clientHeight/2));
  root.querySelector('#zoom-reset')?.addEventListener('click',()=>{camera.setZoom(1);camera.setScroll(0,0);clampCamera();const reset=root.querySelector<HTMLButtonElement>('#zoom-reset');if(reset)reset.textContent='1.0×';});
  const card=root.querySelector<HTMLButtonElement>('#summon-card')!;
  let suppressSummonClick=false;
  card.addEventListener('pointerdown',(event)=>{suppressSummonClick=false;card.setPointerCapture(event.pointerId);card.dataset.startX=String(event.clientX);card.dataset.startY=String(event.clientY);card.dataset.startTime=String(performance.now());});
  card.addEventListener('pointerup',(event)=>{const startX=Number(card.dataset.startX??event.clientX),startY=Number(card.dataset.startY??event.clientY),started=Number(card.dataset.startTime??performance.now());const dx=event.clientX-startX,dy=event.clientY-startY;if(Math.hypot(dx,dy)<28){root.querySelector('#roster-overlay')?.classList.remove('hidden');return;}suppressSummonClick=true;const rect=root.querySelector('#battle-canvas')!.getBoundingClientRect();const startXRatio=Math.max(.08,Math.min(.92,(startX-rect.left)/rect.width));const gameDx=dx/rect.width*360,gameDy=dy/rect.height*worldHeight;const speed=Math.hypot(dx,dy)/Math.max(.06,(performance.now()-started)/1000);scene.launchSummon(startXRatio,gameDx,gameDy,speed);root.querySelector('#summon-tray')?.classList.add('thrown');});
  card.addEventListener('click',(event)=>{if(suppressSummonClick){suppressSummonClick=false;event.preventDefault();return;}root.querySelector('#roster-overlay')?.classList.remove('hidden');});
  root.querySelectorAll<HTMLButtonElement>('[data-summon]').forEach((button)=>button.addEventListener('click',()=>applyPartnerChoice(button.dataset.summon??'',scene)));
  root.querySelector('#dev-partner')?.addEventListener('change',(event)=>applyPartnerChoice((event.target as HTMLSelectElement).value,scene));
  root.querySelector('#close-roster')?.addEventListener('click',()=>root.querySelector('#roster-overlay')?.classList.add('hidden'));
  let catchWasActive=false;
  scene.onUpdate=(state)=>{const e=root.querySelector<HTMLElement>('#enemy-hp'),p=root.querySelector<HTMLElement>('#player-hp'),time=root.querySelector<HTMLElement>('#battle-time');if(e)e.style.width=`${Math.max(0,state.enemyHp)}%`;if(p)p.style.width=`${Math.max(0,state.playerHp)}%`;if(time)time.textContent=`${Math.floor(state.seconds/60)}:${String(Math.floor(state.seconds%60)).padStart(2,'0')}`;const meter=root.querySelector<HTMLElement>('#catch-progress');if(meter)meter.style.width=`${state.catchProgress}%`;const status=root.querySelector<HTMLElement>('#catch-status');if(status)status.textContent=state.catchActive?'Keep close · progress fades when you drift':'Use the stick to close in';const catchButton=root.querySelector<HTMLButtonElement>('#catch-mode');if(catchButton)catchButton.disabled=!state.summoned||profile.gachaCards<1;if(state.summoned)root.querySelector('#summon-tray')?.classList.add('thrown');if(catchWasActive&&!state.catchActive&&!state.result)root.querySelector('#catch-overlay')?.classList.add('hidden');catchWasActive=state.catchActive;if(state.result)finishBattle(state.result,scene);};
  root.querySelector('#catch-mode')?.addEventListener('click',async()=>{if(profile.gachaCards<1)return;try{// The account's balance is authoritative only when there is an account. A player
      // without one spends the device's own count: `consume_gacha_card` is granted to
      // authenticated and revoked from anon, so asking it would fail for them and the
      // catch would be refused with a message about reconnecting to a server they
      // are not using.
      const balance=signedIn?await consumeGachaCard(activeBattleId):null;profile.gachaCards=balance??Math.max(0,profile.gachaCards-1);scene.beginCatch();void save();root.querySelector('#catch-overlay')?.classList.remove('hidden');}catch(error){console.warn('Could not confirm GachaCard balance.',error);const status=root.querySelector<HTMLElement>('#catch-status');if(status)status.textContent='Card balance unavailable. Reconnect and try again.';}});
  root.querySelector('#cancel-catch')?.addEventListener('click',()=>{scene.cancelCatch();root.querySelector('#catch-overlay')?.classList.add('hidden');});
  root.querySelector('#give-up')?.addEventListener('click',()=>scene.giveUp());
  const joy=root.querySelector<HTMLElement>('#joystick');const move=(event:PointerEvent)=>{if(!joy)return;const rect=joy.getBoundingClientRect(),x=(event.clientX-rect.left-rect.width/2)/(rect.width/2),y=(event.clientY-rect.top-rect.height/2)/(rect.height/2);scene.steer(Math.max(-1,Math.min(1,x)),Math.max(-1,Math.min(1,y)));};joy?.addEventListener('pointerdown',(event)=>{joy.setPointerCapture(event.pointerId);move(event);});joy?.addEventListener('pointermove',(event)=>{if(event.buttons)move(event);});joy?.addEventListener('pointerup',()=>scene.steer(0,0));
}

/** Continues to the screen the player was heading for, or the map when there is none. */
function retreatAndContinue(): void {
  const destination = retreatDestination;
  retreatDestination = undefined;
  (destination ?? renderHome)();
}

/**
 * Asks before a battle in progress is abandoned.
 *
 * Leaving the arena is a retreat, so it is confirmed the same way the in-arena
 * Retreat deserves to be: a plain question with one way to stay and one way to
 * go. Confirmations live here rather than on each control because the topbar back
 * button, the bottom navigation and any future route must all behave identically.
 */
function confirmRetreat(destination: () => void): void {
  root.querySelector('#leave-overlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'leave-overlay';
  overlay.className = 'roster-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Leave the battle?');
  overlay.innerHTML = '<div class="roster-modal"><span class="step">LEAVE BATTLE</span><h2>Retreat?</h2><p>You will retreat from the battle. Do you want to proceed?</p><div class="leave-choices"><button class="leave-button leave-retreat" id="leave-retreat" type="button">Retreat</button><button class="leave-button leave-stay" id="leave-stay" type="button">Stay</button></div></div>';
  root.querySelector('.app-shell')?.append(overlay);
  overlay.querySelector('#leave-stay')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#leave-retreat')?.addEventListener('click', () => {
    overlay.remove();
    retreatDestination = destination;
    const scene = activeBattleScene;
    if (scene && !scene.finished) {
      // Routed through the scene so the departure is recorded as a give-up,
      // exactly like the in-arena Retreat button.
      scene.giveUp();
      return;
    }
    // Left during the arena transition, when no scene is live yet: nothing to
    // retreat from, so stop the music and any game, then continue.
    battleInProgress = false;
    stopBattleMusic();
    teardownBattleGame();
    retreatAndContinue();
  });
}

function finishBattle(result: 'captured' | 'defeated' | 'give-up', scene: BattleScene) {
  // The scene's own `finished` flag is already set by the time this runs, so a
  // local flag is what prevents the handler running twice.
  if (battleResultHandled) return;
  battleResultHandled = true;
  // The battle is no longer on screen, so navigation is unguarded from here.
  battleInProgress = false;
  activeBattleScene = undefined;
  // Every ending fades the music out: a catch, a defeat and a retreat all leave
  // the arena. A battle that ends during Wild's intro is covered by the same
  // call, which cancels the loop already scheduled behind the intro.
  stopBattleMusic();
  if (result === 'give-up') {
    teardownBattleGame();
    // A retreat reached by tapping a navigation control continues to that screen;
    // the in-arena Retreat button has no destination and returns to the map.
    retreatAndContinue();
    return;
  }
  retreatDestination = undefined;
  // Only an owned Garaimon is trained or written to. A partner fielded through
  // developer mode has no instance, so it is recorded in the episode as a
  // `dev:`-prefixed participant and leaves the account's roster untouched.
  const activeGaraimon = activeBattlePartner?.instance;
  const partner = activeBattlePartner;
  const capturedSuccessfully = result === 'captured';
  let captured: GaraimonInstance | undefined;
  if (capturedSuccessfully) {
    captured = newGaraimon(candidateId, createSeed());
    profile.garaimon.push(captured);
  }
  // A player's first completed battle always pays exactly one GachaCard, so a
  // roster that starts with none can still make a first catch. Every later
  // battle uses the usual reward roll. A retreat returns above and earns
  // nothing, so it does not use up the guarantee.
  const firstBattle = profile.episodes.length === 0;
  const reward = firstBattle ? { cards: FIRST_BATTLE_CARD_REWARD, streak: 0 } : rollCardReward(profile);
  const episode: BattleEpisode = {
    id: activeBattleId,
    // The board seed, not the arena seed: this is what identifies the encounter
    // that led to the battle, and the arena carries its own seed already.
    seed: boardSeed,
    simulationVersion: SIMULATION_VERSION,
    policyVersion: POLICY_VERSION,
    arena: activeArena!,
    participants: [
      { id: 'wild', speciesId: candidateId, aiProfile: scene.enemyProfile, level: 1 },
      activeGaraimon
        ? { id: activeGaraimon.id, speciesId: activeGaraimon.speciesId, aiProfile: activeGaraimon.aiProfile, level: activeGaraimon.level }
        : partner
          ? { id: `${DEV_SUMMON_PREFIX}${partner.speciesId}`, speciesId: partner.speciesId, aiProfile: partner.profile, level: 1 }
          : { id: 'partner-fallback', speciesId: FALLBACK_SPECIES_ID, aiProfile: scene.playerProfile, level: 1 },
    ],
    events: scene.battleEvents,
    outcome: result,
    endReason: result,
    // Rounded, because the stored column is a whole number of milliseconds and the clock
    // this comes from is not: `scene.seconds` accumulates a frame delta, so it is
    // fractional, and a fractional millisecond made the database refuse the whole battle
    // insert - losing the record and the reward with it. `finalize_battle` now rounds as
    // well, so this is the correct value rather than the only defence.
    durationMs: Math.round(scene.seconds * 1000),
  };
  profile.episodes = [episode, ...profile.episodes].slice(0, 120);
  if (devOverridesActive) console.info('Garaimon development battle summary', summariseEpisode(episode));
  if (activeGaraimon) {
    const training = trainProfile(activeGaraimon.aiProfile, {
      won: capturedSuccessfully,
      damageDealt: (100 - scene.enemyHp) * scene.enemyMax / 100,
      damageReceived: (100 - scene.playerHp) * scene.playerMax / 100,
      abilityUses: scene.abilityUses,
      blocked: scene.blocked,
    });
    activeGaraimon.aiProfile = training.profile;
    scene.trainingNote = training.note;
  }
  teardownBattleGame();
  // Gated on being signed in, not on the cloud merely being configured. A player who
  // chose "play without saved data" has no account to write to, and the reward RPC is
  // closed to them on purpose - `finalize_battle` is granted to authenticated and
  // revoked from anon, so it answers 42501 permission denied. Taking this branch for
  // them therefore cost them the card they had earned *and* raised a false "NOT SAVED"
  // warning, on every battle, for a player with nothing to save to.
  if (supabase && signedIn) {
    shell('<section class="panel"><h2>Saving your field notes…</h2><p class="muted">Recording the encounter and checking your GachaCard reward.</p></section>', 'FIELD STATION');
    void (async () => {
      let cards = 0;
      let recorded = false;
      try {
        // The record and its reward come first. They are the one thing here that cannot
        // be rebuilt by carrying on playing, so they must not sit behind a write that can
        // be. While this called `syncProfile` first, a refused profile write threw before
        // `finalizeBattle` ever ran - so every signed-in battle was lost from the account
        // and paid no GachaCard, silently, with only the save notice to show for it.
        const serverReward = await finalizeBattle(episode);
        // `duplicate` means the reward was already credited by an earlier attempt
        // whose reply never arrived. Adding it again would pay the player twice, so
        // the local balance is left alone; without this check `cards` would be
        // undefined and the balance would land on NaN.
        if (serverReward && !serverReward.duplicate) {
          cards = serverReward.cards;
          profile.gachaCards += cards;
          profile.noCardStreak = serverReward.noCardStreak;
        }
        recorded = true;
      } catch (error) {
        // The episode stays in the local profile, so the work is not lost - but the player
        // is told, and sign-out will keep the device copy rather than delete it. This is a
        // confirmed failure rather than a blip: the battle's result and its reward are the
        // one thing that cannot be rebuilt by carrying on playing.
        console.warn('Battle record or reward sync failed; local episode was retained.', error);
      }
      saveProfile(profile);
      if (recorded) {
        // The account already holds the record and the reward; this brings the rest of the
        // profile up to it, and reports its own failure on its own terms rather than
        // making the battle above look like the thing that went wrong.
        await pushProfileToAccount();
      } else {
        setSaveStatus('unsaved');
      }
      renderResult(result, captured, cards, scene.trainingNote);
    })();
  } else {
    profile.noCardStreak = reward.streak;
    profile.gachaCards += reward.cards;
    void save();
    renderResult(result, captured, reward.cards, scene.trainingNote);
  }
}

function renderResult(result:'captured'|'defeated', creature:GaraimonInstance|undefined,cards:number,note:string){const wild=getCreature(candidateId);shell(`<section class="result-card ${result==='captured'?'capture-result':'defeat-result'}"><div class="result-symbol">${result==='captured'?'✦':'☼'}</div><span class="tag">${result==='captured'?'NEW GARAIMON':'BATTLE COMPLETE'}</span><h1>${result==='captured'?`${wild.name} joined you!`:`Good battle!`}</h1><p>${result==='captured'?'A new friend is ready for the roster.':'The wild Garaimon escaped. Your partner learned from the fight.'}</p>${creature?`<label class="nickname-label" for="nickname">NICKNAME · UP TO 12 LETTERS<input id="nickname" maxlength="12" placeholder="${wild.name}" value=""></label><div class="stats-row"><span>HP <b>${wild.stats.hp}</b></span><span>POWER <b>${wild.stats.power}</b></span><span>STYLE <b>${styleLabels[wild.style]}</b></span></div>`:''}<div class="reward-note">✧ ${cards?`You found ${cards} GachaCard${cards>1?'s':''}.`:'No GachaCard this time.'}</div>${note?`<p class="training-note">${note}</p>`:''}<button class="primary" id="result-continue">Back to map <span>→</span></button></section>`, 'BATTLE COMPLETE');root.querySelector('#result-continue')?.addEventListener('click',()=>{const input=root.querySelector<HTMLInputElement>('#nickname');if(input&&creature)creature.nickname=input.value.slice(0,12);void save();renderHome();});}

function renderCollection(){teardownCollectGame();teardownBattleGame();const rows=profile.garaimon.map(g=>{const c=getCreature(g.speciesId);return `<article class="roster-row"><div class="roster-orb" style="--creature:${color(c.color)};--accent:${color(c.accent)}">✦</div><div><b>${g.nickname||c.name}</b><small>${c.name} · LV ${g.level} · ${styleLabels[g.aiProfile.style]}</small><small>Curiosity ${Math.round(g.aiProfile.curiosity*100)}% · Confidence ${Math.round(g.aiProfile.confidence*100)}%</small></div><span class="rarity ${c.rarity}">${rarityRung(c.rarity).label}</span></article>`}).join('');shell(`<section class="intro"><span class="step">YOUR ROSTER</span><h1>Your<br><em>Garaimon!</em></h1><p>Every battle helps your Garaimon grow its own instincts.</p></section><div class="collection-count"><b>${profile.garaimon.length}</b><span>GARAIMON IN YOUR ROSTER</span></div><div class="roster-list">${rows||'<div class="empty-state">No Garaimon yet. Choose a continent to find your first one!</div>'}</div><button class="primary" id="collection-explore">Explore the map <span>→</span></button>`,'GARAIMON');root.querySelector('#collection-explore')?.addEventListener('click',renderHome);}
/**
 * Signs out and returns to the landing page.
 *
 * The device copy is cleared, so the next person on the device starts fresh - but only
 * once the account is confirmed to hold the progress. This runs a last save first,
 * because clearing a copy the account does not have would delete the player's work.
 * That save must happen before `signOut()`: the write is made as the signed-in user, so
 * after the session ends there would be nothing to write with.
 *
 * A failed sign-out is reported on the button and nothing is cleared, so a half-finished
 * sign-out cannot look like a completed one. A failed save never blocks the sign-out:
 * ending a session is the player's decision, not something a failed save may veto.
 */
async function handleSignOut(){
  const button = root.querySelector<HTMLButtonElement>('#account-signout');
  if (button) button.disabled = true;
  const accountedFor = await pushProfileToAccount({ quiet: true });
  try {
    await signOut();
  } catch (error) {
    console.error('Garaimon: sign-out could not be completed.', error);
    if (button) { button.disabled = false; button.textContent = 'Not signed out · retry'; }
    return;
  }
  signedIn = false;
  // There is no session left to save to, so the save notice goes with it. When the
  // save did fail, the landing page below says so in full - leaving the notice up as
  // well would say the same thing twice.
  setSaveStatus('idle');
  // Developer status belongs to the account that was signed in, so it ends with it.
  // Without this, signing out and then choosing to play without an account would
  // leave the Testing panel drawn and the dev overrides applied for the rest of the
  // session, because both are resolved once at start-up and this screen is reachable
  // without signing in. A stale flag here would be exactly the leak the whitelist
  // exists to prevent, so it is dropped in the same step as the session.
  setDeveloperAccount(false);
  clearDevOverrides();
  if (accountedFor) {
    clearProfile();
    profile = blankProfile();
    renderLanding();
  } else {
    // The account does not have the newest progress, so the device copy is kept. Nothing
    // is said here: the player is not being asked to do anything, and the copy cannot be
    // lost - signing in again compares the two and asks which to keep, which is what
    // `docs/decisions/DEC-011` is for. A paragraph saying so was removed as noise.
    console.warn('Garaimon: sign-out could not save to the account, so the device copy has been kept.');
    renderLanding();
  }
}

/**
 * Ends the account session without touching the device's progress.
 *
 * Reached from GachID while a choice is waiting, for a player who does not want to make
 * it. The session goes, so the question stops being asked, and the device copy stays
 * exactly as it is - because it is this player's own progress. That is what separates
 * this from the sign-out in the panel above, where the account holds a copy and the
 * device's own is cleared. See `docs/decisions/DEC-006-sign-out.md`.
 */
async function abandonAccount(){
  try {
    await signOut();
  } catch (error) {
    console.error('Garaimon: the account could not be signed out, so the choice is still owed.', error);
    // The session is still there, so the question will be asked again. Deliberately not
    // cleared: saying so is better than pretending the account is gone.
    renderLanding({ message: 'Could not sign out of this account. You will be asked again which save to keep.' });
    return;
  }
  pendingAccountId = null;
  signedIn = false;
  renderLanding();
}

/**
 * GachID: the account and profile hub.
 *
 * It carries the account control, the progress counts and the save-data note. It is
 * the durable sign-in and sign-out route: signing in always goes through the landing
 * page, which is the only page that can send a link, and signing out clears the
 * device's local copy and returns to the landing page. Both are confirmed decisions -
 * see `docs/decisions/DEC-005-returning-local-player.md` and
 * `docs/decisions/DEC-006-sign-out.md`, and this screen's own record,
 * `docs/decisions/DEC-007-gachid-account-hub.md`.
 *
 * The Testing panel is rendered only for an account on the developer list, so a
 * normal player never sees a testing affordance in a release build.
 */
function renderProfile(){
  teardownCollectGame();
  teardownBattleGame();
  const dev=loadDevSettings();
  const testing = hasDeveloperAccount()
    ? `<div class="panel"><h2>Testing</h2>${DEV_FLAGS.map((flag)=>`<label class="dev-flag" for="dev-${flag.key}"><input id="dev-${flag.key}" type="checkbox" data-dev-flag="${flag.key}"${dev[flag.key]?' checked':''}><span><b>${flag.label}</b><small>${flag.hint}</small></span></label>`).join('')}${dev.enabled?'<p class="hint">Developer mode is on. Testing helpers are active across the game.</p>':''}</div>`
    : '';
  // Three states, not two. A player who signed in but has not yet chosen which copy to
  // keep is not signed in as far as their progress is concerned - nothing is being
  // written to the account - so the panel must not say "saved to your account", and it
  // must not offer a plain "Sign in" either, because that would send another link for a
  // choice that is already waiting.
  const account = signedIn
    ? '<div class="panel"><h2>Account</h2><p class="muted">Signed in. Your progress is saved to your account.</p><button class="primary" id="account-signout" type="button">Sign out <span>→</span></button></div>'
    : pendingAccountId
      ? '<div class="panel"><h2>Account</h2><p class="muted">Signed in, but this device and your account both hold progress. Nothing is being saved to your account until you choose which to keep.</p><button class="primary" id="account-choose" type="button">Choose which to keep <span>→</span></button><button class="text-button" id="account-abandon" type="button">Stop asking and sign out of this account</button></div>'
      : '<div class="panel"><h2>Account</h2><p class="muted">Playing on this device without an account.</p><button class="primary" id="account-signin" type="button">Sign in <span>→</span></button></div>';
  // Read from whether this player is signed in, not from whether accounts are
  // configured. The cloud being available says nothing about where *this* player's
  // progress is going: a signed-out player's progress goes to the device whatever
  // the build, and the old wording claimed an account save for everyone.
  //
  // The signed-out line is an invitation rather than a description of where the
  // device save lives, which is a confirmed choice and not a slip: see
  // `docs/decisions/DEC-009-sign-in-copy.md`.
  //
  // A save that has not reached the account overrides both lines. "Saved to your
  // account" is the one thing this panel must never say while that is untrue.
  const saveNote = pendingAccountId
    ? 'Your progress is saved on this device. Nothing is being saved to your account until you choose which copy to keep.'
    : saveStatus === 'unsaved'
      ? 'Your latest progress is saved on this device but has not reached your account yet. It will try again on your next change.'
      : signedIn
        ? 'Your progress is saved to your account.'
        : 'Sign in to save your progress.';
  shell(`<section class="intro"><span class="step">GACHID</span><h1>Your <em>GachID</em></h1><p>Your account, progress and save data in one place. Battle decisions and outcomes are saved to operate, balance, and improve the game. Detailed battle records are retained for 12 months, then anonymised for shared game improvement.</p></section><div class="panel"><h2>Progress</h2><div class="record-row"><span>Battles completed</span><b>${profile.episodes.length}</b></div><div class="record-row"><span>Garaimon in roster</span><b>${profile.garaimon.length}</b></div><div class="record-row"><span>GachaCards</span><b>${profile.gachaCards}</b></div></div><div class="panel"><h2>Save data</h2><p class="muted">${saveNote}</p></div>${account}${testing}`,'GACHID');
  root.querySelector('#account-signin')?.addEventListener('click',()=>renderLanding());
  root.querySelector('#account-signout')?.addEventListener('click',()=>void handleSignOut());
  // Picks the deferred choice back up. This is the second way back to the question
  // besides the next start, so "not now" does not mean the choice is lost until the
  // player thinks to close and reopen the app.
  root.querySelector('#account-choose')?.addEventListener('click',()=>{const accountId=pendingAccountId;pendingAccountId=null;if(accountId)void startWithAccount(accountId);});
  // The way out of a choice the player does not want to make. It ends the session only:
  // the device copy is this player's own progress, so unlike the sign-out in the panel
  // above, nothing local is deleted here. That difference is the reason this is a
  // separate handler rather than a reuse of `handleSignOut`.
  root.querySelector('#account-abandon')?.addEventListener('click',()=>void abandonAccount());
  root.querySelectorAll<HTMLInputElement>('[data-dev-flag]').forEach((input)=>input.addEventListener('change',()=>{setDevFlag(input.dataset.devFlag as keyof DevSettings,input.checked);renderProfile();}));
}

window.addEventListener('resize',()=>{if(!desktop&&innerWidth>760&&matchMedia('(pointer:fine)').matches&&!new URLSearchParams(location.search).has('preview'))showDesktopGate();});
if (desktop) {
  showDesktopGate();
} else {
  // The shared placeholder portrait is decoded as the app opens, well before the
  // collection band can ask for a portrait of a creature, so every creature's own
  // coloured version is ready by the time the reel first paints.
  startPlaceholderPortraits();
  // Read the failure from the address before the auth client initialises. On a
  // successful callback the client owns the fragment and rewrites it, and on a
  // failed one nothing rewrites it but `getSession()` still reports no error, so
  // this is the only place the reason exists. See `authLinkFailure`.
  const linkFailure = authLinkFailure();
  if (linkFailure) console.error('Garaimon: the sign-in link did not complete.', linkFailure.detail);
  let sessionAccountId: string | null = null;
  if (supabase) {
    const session = await supabase.auth.getSession();
    sessionAccountId = session.data.session?.user?.id ?? null;
  }
  if (linkFailure) clearAuthLinkParams();
  if (sessionAccountId) {
    await startWithAccount(sessionAccountId);
  } else {
    // Not signed in, so the landing page is always what the site opens to,
    // including for a returning local player who already has a save. The cost is
    // that such a player taps "Or play without saved data" once per visit to
    // reach the map. That entry rule is a confirmed decision, not an inherited
    // default: see `docs/decisions/DEC-005-returning-local-player.md`. GachID's
    // Account panel gives a local player a labelled sign-in route, and a
    // signed-in player the sign-out control (DEC-006).
    renderLanding(linkFailure ? { message: linkFailure.message } : undefined);
  }
}
